import React, { useState, useEffect, useCallback, useRef } from 'react';
import { platformApi, translateApi, PLATFORM_TOKEN_KEY } from '../api/apiClient';
import { API } from '../api/baseURL';
import ImageUploader from '../components/ImageUploader';
import AnalyticsPanel from '../components/AnalyticsPanel';
import BlogManager from '../components/BlogManager';
import VisitorsPanel from '../components/VisitorsPanel';
import { LANGUAGES, ALL_LANGUAGE_CODES, UI_SECTIONS, UI_DEFAULTS, ADMIN_UI_SECTIONS, ADMIN_UI_DEFAULTS, PLATFORM_LANDING_SECTIONS, PLATFORM_LANDING_DEFAULTS } from '../context/LanguageContext';
import { ADMIN_FA } from '../lang/fa';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useCoreT } from '../context/CoreLangContext';

const PLATFORM_PROFILE_KEY = 'nooshin_platform_profile';
const PLANS = ['Basic','Pro','Enterprise'];
const ALL_PERMISSIONS = ['restaurants','owners','customers','admins','settings'];

function load(k) { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } }

const PLAN_FEATURES = [
  { key:'maxRestaurants', label:'Restaurants (-1=∞)', type:'limit' },
  { key:'maxProducts', label:'Products/menu (-1=∞)', type:'limit' },
  { key:'branches', label:'Multi-branch', type:'bool' },
  { key:'events', label:'Events & promos', type:'bool' },
  { key:'aiTools', label:'AI tools', type:'bool' },
  { key:'advancedAnalytics', label:'Advanced analytics', type:'bool' },
  { key:'multiLanguage', label:'Multi-language', type:'bool' },
  { key:'customDomain', label:'Custom domain', type:'bool' },
  { key:'removeBranding', label:'Remove branding', type:'bool' },
  { key:'aiBlog', label:'AI daily SEO blog', type:'bool' },
  { key:'prioritySupport', label:'Priority support', type:'bool' },
];

export default function PlatformAdminDashboard({ admin: initAdmin, onLogout, settings: externalSettings, onSettingsChange }) {
  const T = useCoreT();
  const [admin, setAdmin]     = useState(initAdmin);
  const [tab,   setTab]       = useState('overview');
  const [notif, setNotif]     = useState(null);

  // Data
  const [stats,       setStats]       = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [owners,      setOwners]      = useState([]);
  const [admins,      setAdmins]      = useState([]);
  const [settings,    setSettings]    = useState(null);

  // Modals
  const [adminModal,  setAdminModal]  = useState(null); // null | {mode,data}
  const [restModal,   setRestModal]   = useState(null); // null | restaurant obj

  const isRoot = admin?.role === 'root';
  // The SEO admin is a full-access role (every tool/section/setting) — the only
  // thing reserved for the true root is managing other admin accounts.
  const isSeo  = admin?.role === 'seo';
  const isFull = isRoot || isSeo;
  const can    = (p) => isFull || admin?.permissions?.[p];

  const notify = (msg, type = 'success') => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3500);
  };

  // ── Image localization (mirror Iran-blocked remote images onto our server) ──
  const [localize, setLocalize] = useState(null); // null | status object
  const localizeTimer = useRef(null);
  const pollLocalize = useCallback(async () => {
    try {
      const r = await platformApi.localizeImagesStatus();
      setLocalize(r.status);
      if (r.status?.running) {
        localizeTimer.current = setTimeout(pollLocalize, 1500);
      }
    } catch {}
  }, []);
  const startLocalize = async () => {
    try {
      const r = await platformApi.localizeImages();
      setLocalize(r.status || { running: true, done: 0, total: 0 });
      notify(T.localizeStarted || 'Localizing images…');
      if (localizeTimer.current) clearTimeout(localizeTimer.current);
      localizeTimer.current = setTimeout(pollLocalize, 1200);
    } catch (e) {
      notify(e.message || 'Could not start localization', 'error');
    }
  };
  useEffect(() => {
    // Pick up an in-progress run when the dashboard mounts.
    pollLocalize();
    return () => { if (localizeTimer.current) clearTimeout(localizeTimer.current); };
  }, [pollLocalize]);

  const logout = () => {
    localStorage.removeItem(PLATFORM_TOKEN_KEY);
    localStorage.removeItem(PLATFORM_PROFILE_KEY);
    onLogout();
  };

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try { const r = await platformApi.getStats(); setStats(r.data); } catch {}
  }, []);

  const fetchRestaurants = useCallback(async () => {
    try { const r = await platformApi.getRestaurants(); setRestaurants(r.data); } catch {}
  }, []);

  const fetchOwners = useCallback(async () => {
    try { const r = await platformApi.getOwners(); setOwners(r.data); } catch {}
  }, []);

  const fetchAdmins = useCallback(async () => {
    try { const r = await platformApi.getAdmins(); setAdmins(r.data); } catch {}
  }, []);

  const fetchSettings = useCallback(async () => {
    try { const r = await platformApi.getSettings(); setSettings(r.data); } catch {}
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    if (tab === 'restaurants' && can('restaurants')) fetchRestaurants();
    if (tab === 'owners'      && can('owners'))      fetchOwners();
    if (tab === 'admins'      && isRoot)             fetchAdmins();
    if (tab === 'settings'    && (can('settings')||isRoot)) fetchSettings();
    if (tab === 'seo'         && (can('settings')||isFull)) { fetchSettings(); fetchSeoFiles(); }
    if (tab === 'language'    && (can('settings')||isRoot)) fetchSettings();
  }, [tab]);

  // ── Restaurant actions ───────────────────────────────────────────────────
  const saveRestaurant = async (id, data) => {
    try {
      await platformApi.updateRestaurant(id, data);
      fetchRestaurants();
      notify(T.tRestUpdated);
      setRestModal(null);
    } catch (e) { notify(e.message, 'error'); }
  };

  const deleteRestaurant = async (id, name) => {
    if (!window.confirm(T.confirmDeleteRest(name))) return;
    try {
      await platformApi.deleteRestaurant(id);
      fetchRestaurants();
      notify(T.tRestDeleted);
    } catch (e) { notify(e.message, 'error'); }
  };

  const toggleRestaurantActive = async (r) => {
    try {
      await platformApi.updateRestaurant(r._id, { isActive: !r.isActive });
      fetchRestaurants();
      notify(r.isActive ? T.tRestDeactivated : T.tRestActivated);
    } catch (e) { notify(e.message, 'error'); }
  };

  // ── Owner actions ────────────────────────────────────────────────────────
  const toggleOwnerActive = async (o) => {
    try {
      await platformApi.updateOwner(o._id, { isActive: !o.isActive });
      fetchOwners();
      notify(o.isActive ? T.tOwnerDeactivated : T.tOwnerActivated);
    } catch (e) { notify(e.message, 'error'); }
  };

  const changeOwnerPlan = async (id, plan) => {
    try {
      await platformApi.updateOwner(id, { plan });
      fetchOwners();
      notify(T.tPlanUpdated);
    } catch (e) { notify(e.message, 'error'); }
  };

  // ── Platform admin actions ───────────────────────────────────────────────
  const saveAdmin = async (data) => {
    try {
      if (adminModal.mode === 'edit') {
        await platformApi.updateAdmin(adminModal.data._id, data);
        notify(T.tAdminUpdated);
      } else {
        await platformApi.createAdmin(data);
        notify(T.tAdminCreated);
      }
      fetchAdmins();
      setAdminModal(null);
    } catch (e) { notify(e.message, 'error'); }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm(T.confirmDelete)) return;
    try { await platformApi.deleteAdmin(id); fetchAdmins(); notify(T.tAdminDeleted); }
    catch (e) { notify(e.message, 'error'); }
  };


  // ── Settings ─────────────────────────────────────────────────────────────
  const [settingsForm, setSettingsForm] = useState({});
  const [genBusy, setGenBusy] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);
  const [aiTest, setAiTest] = useState(null);
  const setAi = (k, v) => setSettingsForm(f => ({ ...f, ai: { ...(f.ai || {}), [k]: v } }));
  const setSeo = (k, v) => setSettingsForm(f => ({ ...f, seo: { ...(f.seo || {}), [k]: v } }));
  const [seoSaving, setSeoSaving] = useState(false);
  const [seoSaved, setSeoSaved] = useState(false);
  const saveSeo = async () => {
    setSeoSaving(true); setSeoSaved(false);
    try { await platformApi.updateSettings({ seo: settingsForm.seo || {} }); setSeoSaved(true); setTimeout(() => setSeoSaved(false), 2500); }
    catch (e) { alert(e.message || 'Failed'); }
    finally { setSeoSaving(false); }
  };

  // ── Live crawler files: robots.txt / llms.txt (view + editable overrides) ──
  const [seoFiles, setSeoFiles]       = useState(null);   // { robots:{override,generated}, llms:{…} }
  const [robotsDraft, setRobotsDraft] = useState('');
  const [llmsDraft, setLlmsDraft]     = useState('');
  const [filesLoading, setFilesLoading] = useState(false);
  const [fileSaving, setFileSaving]   = useState('');     // 'robots' | 'llms' | ''
  const [fileSaved, setFileSaved]     = useState('');
  const fetchSeoFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const r = await platformApi.getSeoFiles();
      const d = (r && r.data) || {};
      setSeoFiles(d);
      setRobotsDraft((d.robots && (d.robots.override || d.robots.generated)) || '');
      setLlmsDraft((d.llms && (d.llms.override || d.llms.generated)) || '');
    } catch { /* leave empty */ }
    finally { setFilesLoading(false); }
  }, []);
  const persistSeoFile = async (which, value) => {
    const key = which === 'robots' ? 'robotsTxt' : 'llmsTxt';
    const nextSeo = { ...(settingsForm.seo || {}), [key]: value };
    await platformApi.updateSettings({ seo: nextSeo });
    setSettingsForm(f => ({ ...f, seo: nextSeo }));
    setSeoFiles(s => ({ ...(s || {}), [which]: { ...((s && s[which]) || {}), override: value } }));
  };
  const saveSeoFile = async (which) => {
    setFileSaving(which); setFileSaved('');
    try {
      await persistSeoFile(which, which === 'robots' ? robotsDraft : llmsDraft);
      setFileSaved(which); setTimeout(() => setFileSaved(''), 2500);
    } catch (e) { alert(e.message || 'Failed'); }
    finally { setFileSaving(''); }
  };
  const resetSeoFile = async (which) => {
    setFileSaving(which); setFileSaved('');
    try {
      await persistSeoFile(which, '');
      const gen = (seoFiles && seoFiles[which] && seoFiles[which].generated) || '';
      if (which === 'robots') setRobotsDraft(gen); else setLlmsDraft(gen);
      setFileSaved(which); setTimeout(() => setFileSaved(''), 2500);
    } catch (e) { alert(e.message || 'Failed'); }
    finally { setFileSaving(''); }
  };
  const testAiConn = async () => {
    setAiTesting(true); setAiTest(null);
    try {
      // Save first so the test uses the values currently in the form.
      await platformApi.updateSettings({ ai: settingsForm.ai || {} });
      const r = await platformApi.testAi();
      setAiTest(r.data || r);
    } catch (e) { notify(e.message || T.tLangFailed, 'error'); }
    finally { setAiTesting(false); }
  };
  const generatePostNow = async () => {
    setGenBusy(true);
    try {
      const r = await platformApi.generatePost();
      notify(r?.data?.slug ? T.postGenerated : (r?.message || T.postGenerated));
    } catch (e) { notify(e.message || T.postGenerateFailed, 'error'); }
    finally { setGenBusy(false); }
  };
  useEffect(() => { if (settings) {
    // Pre-fill the local-LLM fields when unset so the AI Local URL isn't left
    // blank (a blank field means the server falls back to its localhost default,
    // which can't reach LM Studio on another machine). Saving then persists the
    // real endpoint, overriding any stale default.
    const ai = settings.ai || {};
    const seededAi = {
      ...ai,
      localUrl:   ai.localUrl   || 'http://192.168.11.165:1234/v1',
      localModel: ai.localModel || 'gemma-3-4b-it',
    };
    setSettingsForm({ ...settings, ai: seededAi });
    // Load platform translations
    const pt = settings.platformTranslations || {};
    const ui = {}, info = {};
    Object.entries(pt).forEach(([lang, data]) => {
      if (data?.ui)            ui[lang]   = data.ui;
      if (data?.platformInfo)  info[lang] = data.platformInfo;
    });
    setPlatUiTrans(ui);
    setPlatInfoTrans(info);
    // Load admin panel translations
    setPlatAdminLang(settings.adminPanelLang || 'en');
    const apt = settings.adminPanelTranslations || {};
    setPlatAdminTrans(apt);
  }}, [settings]);

  // Platform public translations (homepage UI strings + platform name/tagline)
  const [platTransLang,   setPlatTransLang]   = useState('fa');
  const [platUiTrans,     setPlatUiTrans]     = useState({});
  const [platInfoTrans,   setPlatInfoTrans]   = useState({});
  const [platAutoTrans,   setPlatAutoTrans]   = useState({});

  // Admin panel language & translations (for this dashboard's own UI)
  const [platAdminLang,   setPlatAdminLang]   = useState('en');
  const [platAdminTrans,  setPlatAdminTrans]  = useState({});
  const [adminAutoTrans,  setAdminAutoTrans]  = useState({});

  // Pre-fill the translation editor from the LIVE unified store. Without this, a
  // language that was previously the default (its strings live in the unified
  // store / hand-authored catalogue, not in platformTranslations) shows EMPTY
  // fields. We overlay the store under any in-memory edits so nothing is lost.
  useEffect(() => {
    const lang = platTransLang;
    if (!lang) return;
    let cancelled = false;
    fetch('/api/i18n/' + encodeURIComponent(lang))
      .then(r => (r.ok ? r.json() : { strings: {} }))
      .then(d => {
        if (cancelled) return;
        const store = d.strings || {};
        if (!Object.keys(store).length) return;
        setPlatUiTrans(prev => ({ ...prev, [lang]: { ...store, ...(prev[lang] || {}) } }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [platTransLang]);

  // Same fix for the ADMIN-PANEL translation editor: when a non-English admin
  // language is selected, pre-fill empty fields with what the panel actually
  // shows — the built-in Persian catalogue (ADMIN_FA) for fa, plus the unified
  // store — so e.g. Persian never renders blank when it was already complete.
  useEffect(() => {
    const lang = platAdminLang;
    if (!lang || lang === 'en') return;
    let cancelled = false;
    const faBase = lang === 'fa' ? ADMIN_FA : {};
    fetch('/api/i18n/' + encodeURIComponent(lang))
      .then(r => (r.ok ? r.json() : { strings: {} }))
      .then(d => {
        if (cancelled) return;
        const store = d.strings || {};
        setPlatAdminTrans(prev => {
          const merged = { ...prev };
          Object.keys(ADMIN_UI_DEFAULTS).forEach(k => {
            if (!merged[k]) { const v = store[k] || faBase[k]; if (v) merged[k] = v; }
          });
          return merged;
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [platAdminLang]);

  // ── Server-side translate helper (multi-provider + cache, same as admin) ─────
  // Chunked (so a big string set can't 413) and review=true so the AI quality
  // pass + fallback run when the free providers fail. ALWAYS auto-detects the
  // source: the stored text may be in any language (dirty/mixed), so assuming
  // "source = default" makes the model translate the wrong way and the text
  // comes back unchanged (looks like "English by default").
  const translateVia = async (texts, source, target, review = true) => {
    const clean = texts.map(t => (t == null ? '' : String(t)));
    const src = 'auto';
    if (src === target) return clean;
    const CHUNK = 120;
    const out = new Array(clean.length);
    for (let i = 0; i < clean.length; i += CHUNK) {
      const slice = clean.slice(i, i + CHUNK);
      try {
        const res = await translateApi.translate(slice, src, target, review);
        const t = res?.translations;
        for (let j = 0; j < slice.length; j++) {
          out[i + j] = (Array.isArray(t) && t.length === slice.length && t[j]) ? t[j] : slice[j];
        }
      } catch { for (let j = 0; j < slice.length; j++) out[i + j] = slice[j]; }
    }
    return out;
  };
  const autoTranslate = async (text, target, onResult) => {
    if (!text?.trim()) return;
    const source = settingsForm.defaultLanguage || 'en';
    const [t] = await translateVia([text], source, target);
    if (t && t.trim()) onResult(t);
  };

  // ── Platform public UI translation helpers ────────────────────────────────
  const setPlatUiKey = (key, val) => setPlatUiTrans(prev => ({
    ...prev, [platTransLang]: { ...(prev[platTransLang]||{}), [key]: val },
  }));
  const setPlatInfoKey = (field, val) => setPlatInfoTrans(prev => ({
    ...prev, [platTransLang]: { ...(prev[platTransLang]||{}), [field]: val },
  }));

  const autoTranslatePlatUi = async (key, defaultText) => {
    setPlatAutoTrans(p => ({ ...p, [`ui-${key}`]: true }));
    await autoTranslate(defaultText, platTransLang, val => setPlatUiKey(key, val));
    setPlatAutoTrans(p => { const n={...p}; delete n[`ui-${key}`]; return n; });
  };
  const autoTranslatePlatInfo = async (field, text) => {
    setPlatAutoTrans(p => ({ ...p, [`info-${field}`]: true }));
    await autoTranslate(text, platTransLang, val => setPlatInfoKey(field, val));
    setPlatAutoTrans(p => { const n={...p}; delete n[`info-${field}`]; return n; });
  };
  const autoTranslateAllPlatPublic = async () => {
    setPlatAutoTrans({ _all: true });
    // Collect EVERY string and translate in ONE batched (chunked) request. Firing
    // one request per string in parallel means dozens of simultaneous LLM calls
    // to a single local model — they overwhelm it and time out back to English.
    const jobs = [];   // { kind:'info'|'ui', field?|key? }
    const texts = [];
    const push = (text, job) => { if (text && String(text).trim()) { texts.push(text); jobs.push(job); } };
    push(settingsForm.platformName || '',    { kind: 'info', field: 'name' });
    push(settingsForm.platformTagline || '', { kind: 'info', field: 'tagline' });
    PLATFORM_LANDING_SECTIONS.forEach(sec => sec.keys.forEach(({ key, default: def }) => {
      push(def, { kind: 'ui', key });
    }));
    const results = await translateVia(texts, settingsForm.defaultLanguage || 'en', platTransLang);
    results.forEach((val, i) => {
      if (!val || !String(val).trim()) return;
      const job = jobs[i];
      if (job.kind === 'info') setPlatInfoKey(job.field, val);
      else                     setPlatUiKey(job.key, val);
    });
    setPlatAutoTrans({});
    notify(T.tTransDone);
  };

  // Translate the platform public UI + info into EVERY enabled language and save,
  // in one action — the same "Translate All Languages & Save" the menu offers.
  const [platAllLangsBusy, setPlatAllLangsBusy] = useState(false);
  const autoTranslateAllPlatLanguages = async () => {
    const def = settingsForm.defaultLanguage || 'en';
    const targets = (settingsForm.enabledLanguages || []).filter(l => l && l !== def);
    if (targets.length === 0) { notify(T.tEnableOneLang || 'Enable at least one language besides the default first', 'error'); return; }
    setPlatAllLangsBusy(true);
    try {
      const jobs = [];   // { kind, field?|key? }
      const texts = [];
      const push = (text, job) => { if (text && String(text).trim()) { texts.push(text); jobs.push(job); } };
      push(settingsForm.platformName || '',    { kind: 'info', field: 'name' });
      push(settingsForm.platformTagline || '', { kind: 'info', field: 'tagline' });
      PLATFORM_LANDING_SECTIONS.forEach(sec => sec.keys.forEach(({ key, default: def2 }) => push(def2, { kind: 'ui', key })));
      const nextUi = {}, nextInfo = {};
      for (const lang of targets) {
        const results = await translateVia(texts, def, lang);
        const ui = {}, info = {};
        results.forEach((val, i) => {
          if (!val || !String(val).trim()) return;
          const job = jobs[i];
          if (job.kind === 'info') info[job.field] = val; else ui[job.key] = val;
        });
        nextUi[lang] = ui; nextInfo[lang] = info;
      }
      // Merge into state, then persist through the normal save.
      const mergedUi = { ...platUiTrans }, mergedInfo = { ...platInfoTrans };
      for (const lang of targets) {
        mergedUi[lang]   = { ...(mergedUi[lang] || {}),   ...nextUi[lang] };
        mergedInfo[lang] = { ...(mergedInfo[lang] || {}), ...nextInfo[lang] };
      }
      setPlatUiTrans(mergedUi); setPlatInfoTrans(mergedInfo);
      const mergedPlatTrans = { ...(settingsForm.platformTranslations || {}) };
      Object.entries(mergedUi).forEach(([lang, keys]) => { mergedPlatTrans[lang] = { ...(mergedPlatTrans[lang] || {}), ui: keys }; });
      Object.entries(mergedInfo).forEach(([lang, info]) => { mergedPlatTrans[lang] = { ...(mergedPlatTrans[lang] || {}), platformInfo: info }; });
      await platformApi.updateSettings({ ...settingsForm, platformTranslations: mergedPlatTrans });
      notify(T.tTranslatedSaved || 'Translated & saved ✓');
    } catch (e) { notify(e.message || 'Failed', 'error'); }
    finally { setPlatAllLangsBusy(false); }
  };

  // ── Admin panel translation helpers ───────────────────────────────────────
  const setAdminKey = (key, val) => setPlatAdminTrans(prev => ({
    ...prev, [key]: val,
  }));
  const autoTranslateAdminKey = async (key, defaultText) => {
    if (platAdminLang === 'en') { setAdminKey(key, ''); return; }
    setAdminAutoTrans(p => ({ ...p, [key]: true }));
    const [t] = await translateVia([defaultText], 'en', platAdminLang);
    if (t && t.trim() && t !== defaultText) setAdminKey(key, t);
    setAdminAutoTrans(p => { const n={...p}; delete n[key]; return n; });
  };
  const autoTranslateAllAdmin = async () => {
    // English selected = reset: clear overrides, let built-in defaults take over
    if (platAdminLang === 'en') {
      setPlatAdminTrans({});
      notify(T.tResetDefaults);
      return;
    }
    setAdminAutoTrans({ _all: true });
    const keys = [];
    const texts = [];
    ADMIN_UI_SECTIONS.forEach(sec => sec.keys.forEach(({ key, default: def }) => {
      keys.push(key); texts.push(def);
    }));
    const results = await translateVia(texts, 'en', platAdminLang);
    results.forEach((t, i) => { if (t && t.trim() && t !== texts[i]) setAdminKey(keys[i], t); });
    setAdminAutoTrans({});
    notify(T.tAdminTranslated+' — '+(LANGUAGES[platAdminLang]?.name||''));
  };

  // Top-bar dashboard-language switch — any language. English and Persian use
  // the built-in catalogues; any other language auto-translates the whole
  // dashboard string set (API → AI fallback) and stores it in coreTranslations.
  // The whole dashboard UI then renders in that language (overlaid on English).
  const [quickLangBusy, setQuickLangBusy] = useState(false);
  const [reviewBusy, setReviewBusy] = useState({});   // { lang: true } while polishing
  const currentCoreLang = settingsForm.coreLang
    || (settingsForm.coreMode === 'persian' ? 'fa' : 'en');

  // Run the AI quality-review pass over a machine-translated language and store
  // the polished strings (local AI first, then cloud — see Services/aiService).
  const polishLang = async (langCode) => {
    if (reviewBusy[langCode]) return;
    setReviewBusy(b => ({ ...b, [langCode]: true }));
    try {
      const r = await platformApi.reviewUiLang(langCode);
      const n = (r && r.changed) || 0;
      notify(n > 0 ? T.polishDoneMsg.replace('{n}', n) : T.polishNoneMsg);
    } catch (e) {
      notify(e.message || T.tLangFailed, 'error');
    } finally {
      setReviewBusy(b => ({ ...b, [langCode]: false }));
    }
  };

  const quickSetCoreLang = async (langCode) => {
    if (quickLangBusy || langCode === currentCoreLang) return;
    setQuickLangBusy(true);
    // Reflect the choice in the dropdown immediately so it never shows a stale value.
    setSettingsForm(f => ({ ...f, coreLang: langCode, coreMode: langCode === 'fa' ? 'persian' : (langCode === 'en' ? 'global' : f.coreMode) }));
    try {
      // EN/FA have hand-crafted catalogues → no translation needed.
      if (langCode === 'en' || langCode === 'fa') {
        await platformApi.updateSettings({
          coreLang: langCode,
          coreMode: langCode === 'fa' ? 'persian' : 'global',
        });
      } else {
        // ONE action, ONE synced system: gather the English base of EVERY
        // surface (platform dashboard + home + restaurant-admin + business +
        // customer-menu chrome), translate it once, and store it in the unified
        // store. Every surface then renders in the new language — no per-page
        // translation step to think about.
        const SKIP = new Set(['dir', 'lang', 'fontFamily', 'titleFont']);
        const stringsMod = await import('../lang/strings');
        const lc = await import('../context/LanguageContext');
        const bc = await import('../context/BusinessLangContext');
        const homeEn = await fetch('/api/i18n/en')
          .then(r => (r.ok ? r.json() : { strings: {} })).then(d => d.strings || {}).catch(() => ({}));

        const baseMap = {};
        const add = (obj) => obj && Object.keys(obj).forEach(k => {
          if (!SKIP.has(k) && typeof obj[k] === 'string' && obj[k] && baseMap[k] === undefined) baseMap[k] = obj[k];
        });
        add((stringsMod.default || stringsMod).global);
        add(lc.UI_DEFAULTS); add(lc.ADMIN_UI_DEFAULTS); add(bc.BIZ_UI_DEFAULTS); add(homeEn);

        const keys = Object.keys(baseMap);
        const texts = keys.map(k => baseMap[k]);
        const out = await translateVia(texts, 'en', langCode);   // API → AI fallback (server)
        const map = {};
        keys.forEach((k, i) => { if (out[i] && String(out[i]).trim()) map[k] = out[i]; });

        await platformApi.setUiTranslations(langCode, map);       // unified store (all surfaces)
        await platformApi.updateSettings({ coreLang: langCode });
      }
      notify(T.tDashLangUpdated);
      setTimeout(() => window.location.reload(), 600);  // reload so the whole dashboard re-renders in the new language
    } catch (e) {
      notify(e.message || T.tLangFailed, 'error');
      setQuickLangBusy(false);   // re-enable on failure so it never "hangs"
    }
  };

  const saveSettings = async () => {
    try {
      // Merge platform public translations
      const mergedPlatTrans = { ...(settingsForm.platformTranslations || {}) };
      Object.entries(platUiTrans).forEach(([lang, keys]) => {
        mergedPlatTrans[lang] = { ...(mergedPlatTrans[lang]||{}), ui: keys };
      });
      Object.entries(platInfoTrans).forEach(([lang, info]) => {
        mergedPlatTrans[lang] = { ...(mergedPlatTrans[lang]||{}), platformInfo: info };
      });
      await platformApi.updateSettings({
        ...settingsForm,
        platformTranslations:   mergedPlatTrans,
        adminPanelLang:         platAdminLang,
        adminPanelTranslations: platAdminTrans,
      });
      notify(T.tSettingsSaved);
    } catch (e) { notify(e.message, 'error'); }
  };

  const togglePlatformLang = (code) => {
    setSettingsForm(f => {
      const has = (f.enabledLanguages||[]).includes(code);
      if (has && f.enabledLanguages.length === 1) return f;
      const next = has ? f.enabledLanguages.filter(c => c!==code) : [...(f.enabledLanguages||[]),code];
      return { ...f, enabledLanguages: next };
    });
  };

  // ── Sidebar tabs ─────────────────────────────────────────────────────────
  const tabs = [
    { id: 'overview',     icon: '📊', label: T.overview,      show: true },
    { id: 'restaurants',  icon: '🍽️', label: T.restaurants,   show: can('restaurants') },
    { id: 'owners',       icon: '👔', label: T.businessOwners, show: can('owners') },
    { id: 'admins',       icon: '🛡️', label: T.platformAdmins, show: isRoot },
    { id: 'settings',     icon: '⚙️', label: T.settings,       show: can('settings') || isRoot },
    { id: 'seo',          icon: '🔍', label: T.seo || 'SEO & Google', show: can('settings') || isFull },
    { id: 'visitors',     icon: '👣', label: T.visitors || 'Visitors', show: true },
    { id: 'analytics',    icon: '📈', label: T.analytics,      show: true },
    { id: 'blog',         icon: '📝', label: T.blog || 'Blog',  show: can('settings') || isRoot },
    { id: 'language',     icon: '🌐', label: T.language,        show: can('settings') || isRoot },
    { id: 'plat-trans',   icon: '🔤', label: T.translations,    show: can('settings') || isRoot },
    { id: 'admin-lang',   icon: '🖥️', label: T.adminLanguage,  show: can('settings') || isRoot },
  ].filter(t => t.show);

  const pri = '#6366f1'; // platform accent — indigo

  return (
    <div className="pa-root" style={{ '--pa': pri }}>
      {notif && <div className={`pa-notif ${notif.type}`}>{notif.msg}</div>}

      {/* Modals */}
      {adminModal && <AdminModal modal={adminModal} onSave={saveAdmin} onClose={() => setAdminModal(null)} />}
      {restModal  && <RestaurantEditModal rest={restModal} onSave={saveRestaurant} onClose={() => setRestModal(null)} />}

      {/* Sidebar */}
      <aside className="pa-sidebar">
        <div className="pa-brand">
          <span style={{fontSize:'2rem'}}>🏛️</span>
          <div>
            <div className="pa-brand-name">{T.platformName}</div>
            <div className="pa-brand-role">{isRoot ? T.rootAdmin : T.platformAdmin}</div>
          </div>
        </div>
        <nav className="pa-nav">
          {tabs.map(t => (
            <button key={t.id} className={`pa-nav-btn ${tab===t.id?'active':''}`} onClick={() => setTab(t.id)}>
              <span>{t.icon}</span><span>{t.label}</span>
            </button>
          ))}
        </nav>
        <div className="pa-sidebar-foot">
          <div className="pa-admin-info">
            <div className="pa-admin-name">{admin?.name}</div>
            <div className="pa-admin-email">{admin?.email}</div>
          </div>
          <button className="pa-logout" onClick={logout}>🚪 {T.logout}</button>
        </div>
      </aside>

      {/* Main */}
      <main className="pa-main">
        <div className="pa-topbar">
          <h1 className="pa-topbar-title">{tabs.find(t=>t.id===tab)?.label || T.overview}</h1>
          <div className="pa-topbar-right">
            {/* Stage 5: preview the public platform home in a language */}
            {settingsForm.languageMode === 'multi' && (settingsForm.enabledLanguages || []).length > 1 && (
              <div className="pa-tb-group" title="Preview platform home in a language">
                <span>👁️</span>
                <select className="pa-tb-select" defaultValue=""
                  onChange={e => { if (e.target.value) { window.open(`/?lang=${e.target.value}`, '_blank'); e.target.value=''; } }}>
                  <option value="" disabled>Preview home…</option>
                  {(settingsForm.enabledLanguages || []).map(code => (
                    <option key={code} value={code}>{LANGUAGES[code]?.flag} {LANGUAGES[code]?.name}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Dashboard language now lives in the Language tab (single place). */}
          </div>
        </div>

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          <div className="pa-content">
            <div className="pa-stats">
              {[
                { icon:'🍽️', label: T.totalRestaurants,    val: stats?.restaurants  ?? '…' },
                { icon:'👔', label: T.totalOwners,          val: stats?.owners       ?? '…' },
                { icon:'👥', label: T.totalCustomers,       val: stats?.customers    ?? '…' },
                { icon:'🛡️', label: T.totalPlatformAdmins, val: stats?.platformAdmins ?? '…' },
              ].map(s => (
                <div key={s.label} className="pa-stat-card">
                  <div className="pa-stat-icon">{s.icon}</div>
                  <div className="pa-stat-val">{s.val}</div>
                  <div className="pa-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="pa-card">
              <h3>{T.quickActions}</h3>
              {can('restaurants') && <button className="pa-quick" onClick={() => setTab('restaurants')}>{T.manageRestaurants}</button>}
              {can('owners')      && <button className="pa-quick" onClick={() => setTab('owners')}>{T.manageOwners}</button>}
              {isRoot             && <button className="pa-quick" onClick={() => { setTab('admins'); }}>{T.manageAdmins}</button>}
              {(can('settings')||isRoot) && <button className="pa-quick" onClick={() => setTab('language')}>{T.languageSettings}</button>}
            </div>
          </div>
        )}

        {/* ── RESTAURANTS ── */}
        {tab === 'restaurants' && (
          <div className="pa-content">
            <div className="pa-tab-bar"><span className="pa-count">{T.restaurantCount(restaurants.length)}</span></div>
            <div className="pa-list">
              {restaurants.map(r => (
                <div key={r._id} className="pa-rest-row">
                  <div className="pa-rest-info">
                    <div className="pa-rest-name">{r.name}</div>
                    <div className="pa-rest-meta">
                      <span className="pa-badge">{r.slug}</span>
                      {r.cuisine && <span>{r.cuisine}</span>}
                      <span>{T.owner}: {r.owner?.ownerName || r.owner?.email || '—'}</span>
                    </div>
                  </div>
                  <div className="pa-rest-lang">
                    <span className="pa-lang-badge">{r.languageMode === 'multi' ? '🌐 Multi-lang' : '🔤 '+( r.defaultLanguage||'en').toUpperCase()}</span>
                  </div>
                  <button className={`pa-active-badge ${r.isActive?'on':'off'}`} onClick={() => toggleRestaurantActive(r)}>
                    {r.isActive ? `✓ ${T.active}` : `✗ ${T.inactive}`}
                  </button>
                  <div className="pa-row-actions">
                    <button className="pa-icon-btn" title="Edit" onClick={() => setRestModal(r)}>✏️</button>
                    {isRoot && <button className="pa-icon-btn danger" title="Delete" onClick={() => deleteRestaurant(r._id, r.name)}>🗑️</button>}
                  </div>
                </div>
              ))}
              {restaurants.length === 0 && <div className="pa-empty">{T.noRestaurants}</div>}
            </div>
          </div>
        )}

        {/* ── OWNERS ── */}
        {tab === 'owners' && (
          <div className="pa-content">
            <div className="pa-tab-bar"><span className="pa-count">{T.ownerCount(owners.length)}</span></div>
            <div className="pa-list">
              {owners.map(o => (
                <div key={o._id} className="pa-owner-row">
                  <div className="pa-rest-info">
                    <div className="pa-rest-name">{o.ownerName}</div>
                    <div className="pa-rest-meta">
                      <span>{o.email}</span>
                      {o.phone && <span>{o.phone}</span>}
                      <span>{(o.restaurants||[]).length} {T.restaurants}</span>
                    </div>
                  </div>
                  <select className="pa-plan-select" value={o.plan||'Basic'} onChange={e => changeOwnerPlan(o._id, e.target.value)}>
                    {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <button className={`pa-active-badge ${o.isActive?'on':'off'}`} onClick={() => toggleOwnerActive(o)}>
                    {o.isActive ? `✓ ${T.active}` : T.suspended}
                  </button>
                </div>
              ))}
              {owners.length === 0 && <div className="pa-empty">{T.noOwners}</div>}
            </div>
          </div>
        )}

        {/* ── PLATFORM ADMINS ── */}
        {tab === 'admins' && isRoot && (
          <div className="pa-content">
            <div className="pa-tab-bar">
              <span className="pa-count">{admins.length} {T.platformAdmins}</span>
              <button className="pa-add-btn" onClick={() => setAdminModal({ mode:'add', data:null })}>{T.newAdmin}</button>
            </div>
            <div className="pa-list">
              {admins.map(a => (
                <div key={a._id} className="pa-admin-row">
                  <div className="pa-rest-info">
                    <div className="pa-rest-name">
                      {a.role==='root' ? '👑 ' : '🛡️ '}{a.name}
                    </div>
                    <div className="pa-rest-meta">
                      <span>{a.email}</span>
                      <span className={`pa-role-badge ${a.role}`}>{a.role}</span>
                      {a.lastLogin && <span>{T.lastLogin}: {new Date(a.lastLogin).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  {a.role !== 'root' && (
                    <div className="pa-perms">
                      {ALL_PERMISSIONS.map(p => (
                        <span key={p} className={`pa-perm-tag ${a.permissions?.[p] ? 'on' : 'off'}`}>{p}</span>
                      ))}
                    </div>
                  )}
                  <button className={`pa-active-badge ${a.isActive?'on':'off'}`}
                    onClick={() => platformApi.updateAdmin(a._id, {isActive:!a.isActive}).then(fetchAdmins)}>
                    {a.isActive ? `✓ ${T.active}` : `✗ ${T.inactive}`}
                  </button>
                  <div className="pa-row-actions">
                    {a.role !== 'root' && <button className="pa-icon-btn" title="Edit" onClick={() => setAdminModal({mode:'edit',data:a})}>✏️</button>}
                    {a.role !== 'root' && <button className="pa-icon-btn danger" title="Delete" onClick={() => deleteAdmin(a._id)}>🗑️</button>}
                  </div>
                </div>
              ))}
              {admins.length === 0 && <div className="pa-empty">{T.noAdmins}</div>}
            </div>
          </div>
        )}

        {/* ── SETTINGS ── */}
        {tab === 'settings' && settings && (
          <div className="pa-content">
            <div className="pa-settings-form">
              <div className="pa-settings-grid">
                <div className="pa-field full">
                  <label>{T.platformLogo || 'Platform Logo'}</label>
                  <div style={{maxWidth:220}}>
                    <ImageUploader
                      aspectRatio={1}
                      label={T.platformLogo || 'Platform Logo'}
                      currentUrl={settingsForm.logo || ''}
                      imageType="logo"
                      uploadContext={{ type: 'platform-logo', name: 'logo' }}
                      onUploaded={url => setSettingsForm(f => ({ ...f, logo: url }))}
                    />
                  </div>
                </div>
                <div className="pa-field full">
                  <label>{T.platformFavicon || 'Favicon (browser tab icon)'}</label>
                  <div style={{maxWidth:120}}>
                    <ImageUploader
                      aspectRatio={1}
                      label={T.platformFavicon || 'Favicon'}
                      currentUrl={settingsForm.favicon || ''}
                      imageType="logo"
                      uploadContext={{ type: 'platform-favicon', name: 'favicon' }}
                      onUploaded={url => setSettingsForm(f => ({ ...f, favicon: url }))}
                    />
                  </div>
                </div>
                <div className="pa-field full">
                  <label>{T.platformNameLabel}</label>
                  <input className="pa-input" value={settingsForm.platformName||''} onChange={e=>setSettingsForm(f=>({...f,platformName:e.target.value}))} />
                </div>
                <div className="pa-field full">
                  <label>{T.platformTagline}</label>
                  <input className="pa-input" value={settingsForm.platformTagline||''} onChange={e=>setSettingsForm(f=>({...f,platformTagline:e.target.value}))} />
                </div>
                <div className="pa-field">
                  <label>{T.primaryColor}</label>
                  <div className="adm-color-row" style={{display:'flex',gap:'.5rem',alignItems:'center'}}>
                    <input type="color" value={settingsForm.primaryColor||'#c8a96e'} onChange={e=>setSettingsForm(f=>({...f,primaryColor:e.target.value}))} style={{width:44,height:40,border:'none',borderRadius:8,background:'none',cursor:'pointer'}} />
                    <input className="pa-input" value={settingsForm.primaryColor||''} onChange={e=>setSettingsForm(f=>({...f,primaryColor:e.target.value}))} />
                  </div>
                </div>
                <div className="pa-field">
                  <label>{T.accentColor}</label>
                  <div className="adm-color-row" style={{display:'flex',gap:'.5rem',alignItems:'center'}}>
                    <input type="color" value={settingsForm.accentColor||'#a0845a'} onChange={e=>setSettingsForm(f=>({...f,accentColor:e.target.value}))} style={{width:44,height:40,border:'none',borderRadius:8,background:'none',cursor:'pointer'}} />
                    <input className="pa-input" value={settingsForm.accentColor||''} onChange={e=>setSettingsForm(f=>({...f,accentColor:e.target.value}))} />
                  </div>
                </div>
                <div className="pa-field">
                  <label>{T.baseMode || T.systemMode}</label>
                  <div className="pa-toggle-row">
                    {['dark','light'].map(m => (
                      <button key={m} className={`pa-toggle ${(settingsForm.themeMode||'dark')===m?'on':''}`}
                        onClick={()=>setSettingsForm(f=>({...f,themeMode:m}))}>
                        {m==='dark' ? '🌙' : '☀️'} {m==='dark' ? (T.dark||'Dark') : (T.light||'Light')}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pa-field">
                  <label>{T.publicRegistration}</label>
                  <div className="pa-toggle-row">
                    <button className={`pa-toggle ${settingsForm.allowPublicRegister?'on':''}`}
                      onClick={()=>setSettingsForm(f=>({...f,allowPublicRegister:!f.allowPublicRegister}))}>
                      {settingsForm.allowPublicRegister ? `✓ ${T.enabled}` : `✗ ${T.disabled}`}
                    </button>
                    <span className="pa-toggle-hint">{T.allowSelfRegister}</span>
                  </div>
                </div>
                <div className="pa-field">
                  <label>{T.emailAuth}</label>
                  <div className="pa-toggle-row">
                    <button className={`pa-toggle ${settingsForm.emailAuthEnabled?'on':''}`}
                      onClick={()=>setSettingsForm(f=>({...f,emailAuthEnabled:!f.emailAuthEnabled}))}>
                      {settingsForm.emailAuthEnabled ? `✓ ${T.enabled}` : `✗ ${T.disabled}`}
                    </button>
                    <span className="pa-toggle-hint">{T.emailAuthHint}</span>
                  </div>
                </div>
                <div className="pa-field">
                  <label>{T.aiBlog}</label>
                  <div className="pa-toggle-row">
                    <button className={`pa-toggle ${settingsForm.aiBlogEnabled?'on':''}`}
                      onClick={()=>setSettingsForm(f=>({...f,aiBlogEnabled:!f.aiBlogEnabled}))}>
                      {settingsForm.aiBlogEnabled ? `✓ ${T.enabled}` : `✗ ${T.disabled}`}
                    </button>
                    <span className="pa-toggle-hint">{T.aiBlogHint}</span>
                  </div>
                  <div className="pa-toggle-row" style={{marginTop:'.5rem'}}>
                    <button className="pa-toggle" disabled={genBusy} onClick={generatePostNow}>
                      {genBusy ? `⏳ ${T.generating}` : `✨ ${T.generatePostNow}`}
                    </button>
                  </div>
                </div>

                {/* ── Growth: trials & referrals ── */}
                <div className="pa-field" style={{gridColumn:'1 / -1'}}>
                  <label>🚀 {T.growthHeading}</label>
                </div>
                <div className="pa-field">
                  <label>{T.trialDays}</label>
                  <input className="pa-input" type="number" min="0" value={settingsForm.trialDays ?? 14}
                    onChange={e=>setSettingsForm(f=>({...f,trialDays:Math.max(0,Number(e.target.value))}))} />
                  <span className="pa-toggle-hint">{T.trialDaysHint}</span>
                </div>
                <div className="pa-field">
                  <label>{T.freeMenu}</label>
                  <div className="pa-toggle-row">
                    <button className={`pa-toggle ${settingsForm.freeMenuEnabled!==false?'on':''}`}
                      onClick={()=>setSettingsForm(f=>({...f,freeMenuEnabled:!(f.freeMenuEnabled!==false)}))}>
                      {settingsForm.freeMenuEnabled!==false ? `✓ ${T.enabled}` : `✗ ${T.disabled}`}
                    </button>
                    <span className="pa-toggle-hint">{T.freeMenuHint}</span>
                  </div>
                </div>
                <div className="pa-field">
                  <label>{T.referralProgram}</label>
                  <div className="pa-toggle-row">
                    <button className={`pa-toggle ${settingsForm.referralEnabled!==false?'on':''}`}
                      onClick={()=>setSettingsForm(f=>({...f,referralEnabled:!(f.referralEnabled!==false)}))}>
                      {settingsForm.referralEnabled!==false ? `✓ ${T.enabled}` : `✗ ${T.disabled}`}
                    </button>
                    <span className="pa-toggle-hint">{T.referralProgramHint}</span>
                  </div>
                </div>
                <div className="pa-field">
                  <label>{T.referralRewardDays}</label>
                  <input className="pa-input" type="number" min="0" value={settingsForm.referralRewardDays ?? 30}
                    onChange={e=>setSettingsForm(f=>({...f,referralRewardDays:Math.max(0,Number(e.target.value))}))} />
                </div>
                <div className="pa-field">
                  <label>{T.refereeRewardDays}</label>
                  <input className="pa-input" type="number" min="0" value={settingsForm.refereeRewardDays ?? 14}
                    onChange={e=>setSettingsForm(f=>({...f,refereeRewardDays:Math.max(0,Number(e.target.value))}))} />
                </div>

                <div className="pa-field">
                  <label>{T.systemMode}</label>
                  <div className="pa-toggle-row">
                    <button className={`pa-toggle ${(settingsForm.systemMode||'development')==='production'?'on':''}`}
                      onClick={()=>setSettingsForm(f=>({...f,systemMode:(f.systemMode==='production'?'development':'production')}))}>
                      {(settingsForm.systemMode||'development')==='production' ? `🚀 ${T.production}` : `🛠️ ${T.development}`}
                    </button>
                    <span className="pa-toggle-hint">{T.systemModeHint}</span>
                  </div>
                </div>
                <div className="pa-field">
                  <label>{T.maintenanceMode}</label>
                  <div className="pa-toggle-row">
                    <button className={`pa-toggle ${settingsForm.maintenanceMode?'on':''}`}
                      style={settingsForm.maintenanceMode?{background:'rgba(239,68,68,.2)',color:'#fca5a5'}:{}}
                      onClick={()=>setSettingsForm(f=>({...f,maintenanceMode:!f.maintenanceMode}))}>
                      {settingsForm.maintenanceMode ? T.on : T.off}
                    </button>
                    <span className="pa-toggle-hint">{T.maintenanceModeHint}</span>
                  </div>
                </div>

                {/* ── Image localization (Iran-blocked image mirror) ── */}
                <div className="pa-field" style={{gridColumn:'1 / -1'}}>
                  <label>🖼️ {T.localizeImages}</label>
                  <div className="pa-toggle-row" style={{flexWrap:'wrap'}}>
                    <button className="pa-toggle" disabled={localize?.running} onClick={startLocalize}>
                      {localize?.running ? `⏳ ${T.localizeRunning}` : `⬇️ ${T.localizeImagesBtn}`}
                    </button>
                    <span className="pa-toggle-hint">{T.localizeImagesHint}</span>
                  </div>
                  {localize && (localize.running || localize.finishedAt) && (
                    <div className="pa-localize-status">
                      {localize.total > 0 && (
                        <div className="pa-localize-bar">
                          <div className="pa-localize-bar-fill" style={{width: `${Math.round((localize.done / Math.max(1, localize.total)) * 100)}%`}} />
                        </div>
                      )}
                      <span className="pa-toggle-hint">
                        {localize.running ? `⏳ ${localize.done}/${localize.total}` : `✅ ${T.localizeDone}`}
                        {'  ·  '}⬇️ {localize.downloaded || 0}
                        {'  ·  '}♻️ {localize.skipped || 0}
                        {'  ·  '}✏️ {localize.docsUpdated || 0}
                        {(localize.failed > 0) ? `  ·  ⚠️ ${localize.failed}` : ''}
                      </span>
                    </div>
                  )}
                </div>

                {/* ── AI / LLM endpoints ── */}
                <div className="pa-field" style={{gridColumn:'1 / -1'}}>
                  <label>🤖 {T.aiEndpoints}</label>
                  <span className="pa-toggle-hint">{T.aiEndpointsHint}</span>
                </div>
                <div className="pa-field" style={{gridColumn:'1 / -1'}}>
                  <label>{T.aiLocalSection}</label>
                </div>
                <div className="pa-field">
                  <label>{T.aiLocalUrl}</label>
                  <input className="pa-input" value={settingsForm.ai?.localUrl ?? ''} placeholder="http://192.168.11.165:1234/v1"
                    onChange={e=>setAi('localUrl', e.target.value)} />
                  <span className="pa-toggle-hint">{T.aiLocalUrlHint}</span>
                </div>
                <div className="pa-field">
                  <label>{T.aiLocalModel}</label>
                  <input className="pa-input" value={settingsForm.ai?.localModel ?? ''} placeholder="gemma-3-4b-it"
                    onChange={e=>setAi('localModel', e.target.value)} />
                </div>
                <div className="pa-field">
                  <label>{T.aiLocalFirst}</label>
                  <div className="pa-toggle-row">
                    <button className={`pa-toggle ${(settingsForm.ai?.localFirst!==false)?'on':''}`}
                      onClick={()=>setAi('localFirst', !(settingsForm.ai?.localFirst!==false))}>
                      {(settingsForm.ai?.localFirst!==false) ? `✓ ${T.enabled}` : `✗ ${T.disabled}`}
                    </button>
                    <span className="pa-toggle-hint">{T.aiLocalFirstHint}</span>
                  </div>
                </div>
                <div className="pa-field" style={{gridColumn:'1 / -1'}}>
                  <label>{T.aiCloudSection}</label>
                </div>
                <div className="pa-field">
                  <label>{T.aiCloudBase}</label>
                  <input className="pa-input" value={settingsForm.ai?.cloudBase ?? ''} placeholder="https://ai.liara.ir/api/…/v1"
                    onChange={e=>setAi('cloudBase', e.target.value)} />
                </div>
                <div className="pa-field">
                  <label>{T.aiCloudModel}</label>
                  <input className="pa-input" value={settingsForm.ai?.cloudModel ?? ''} placeholder="google/gemini-2.5-flash"
                    onChange={e=>setAi('cloudModel', e.target.value)} />
                </div>
                <div className="pa-field">
                  <label>{T.aiCloudKey}</label>
                  <input className="pa-input" type="password" autoComplete="new-password" value={settingsForm.ai?.cloudKey ?? ''} placeholder="••••••••"
                    onChange={e=>setAi('cloudKey', e.target.value)} />
                </div>
                <div className="pa-field" style={{gridColumn:'1 / -1'}}>
                  <div className="pa-toggle-row">
                    <button className="pa-toggle" disabled={aiTesting} onClick={testAiConn}>
                      {aiTesting ? `⏳ ${T.aiTesting}` : `🔌 ${T.aiTestConnection}`}
                    </button>
                    {aiTest && (
                      <span className="pa-toggle-hint" style={{whiteSpace:'normal'}}>
                        {aiTest.local?.disabled ? `⚪ ${T.aiTestLocalOff}` : aiTest.local?.ok ? `🟢 ${T.aiTestLocalOk} (${aiTest.local.ms}ms)` : `🔴 ${T.aiTestLocalFail}${aiTest.local?.error ? ' — ' + aiTest.local.error : ''}`}
                        {'  ·  '}
                        {aiTest.cloud?.ok ? `🟢 ${T.aiTestCloudOk} (${aiTest.cloud.ms}ms)` : `🔴 ${T.aiTestCloudFail}${aiTest.cloud?.error ? ' — ' + aiTest.cloud.error : ''}`}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {/* ── Subscription plans editor ── */}
              <div className="pa-plans-editor">
                <h3 className="pa-plans-h3">💎 {T.plansEditor || 'Subscription Plans'}</h3>
                <p className="pa-toggle-hint">{T.plansEditorHint || 'Edit prices and toggle which features each plan unlocks. Saved with platform settings.'}</p>
                {(settingsForm.plans || []).map((pl, idx) => (
                  <div key={pl.id || idx} className="pa-plan-edit">
                    <div className="pa-plan-edit-head">
                      <input className="pa-input pa-plan-name" value={pl.name || ''}
                        onChange={e => setSettingsForm(f => { const plans=[...(f.plans||[])]; plans[idx]={...plans[idx], name:e.target.value}; return {...f, plans}; })} />
                      <div className="pa-plan-price-wrap">
                        <input className="pa-input pa-plan-price-in" type="number" value={pl.price ?? 0}
                          onChange={e => setSettingsForm(f => { const plans=[...(f.plans||[])]; plans[idx]={...plans[idx], price:Number(e.target.value)}; return {...f, plans}; })} />
                        <input className="pa-input pa-plan-cur" value={pl.currency || 'USD'}
                          onChange={e => setSettingsForm(f => { const plans=[...(f.plans||[])]; plans[idx]={...plans[idx], currency:e.target.value}; return {...f, plans}; })} />
                        <span className="pa-plan-int">/{pl.interval || 'mo'}</span>
                      </div>
                      <label className="pa-plan-flag"><input type="checkbox" checked={!!pl.highlight}
                        onChange={e => setSettingsForm(f => { const plans=[...(f.plans||[])]; plans[idx]={...plans[idx], highlight:e.target.checked}; return {...f, plans}; })} /> Popular</label>
                      <label className="pa-plan-flag"><input type="checkbox" checked={pl.active !== false}
                        onChange={e => setSettingsForm(f => { const plans=[...(f.plans||[])]; plans[idx]={...plans[idx], active:e.target.checked}; return {...f, plans}; })} /> Active</label>
                    </div>
                    <div className="pa-plan-feats">
                      {PLAN_FEATURES.map(ft => (
                        <div key={ft.key} className="pa-plan-feat">
                          <span className="pa-plan-feat-lbl">{ft.label}</span>
                          {ft.type === 'bool' ? (
                            <button className={`pa-toggle sm ${pl.features?.[ft.key] ? 'on' : ''}`}
                              onClick={() => setSettingsForm(f => { const plans=[...(f.plans||[])]; const features={...(plans[idx].features||{})}; features[ft.key]=!features[ft.key]; plans[idx]={...plans[idx], features}; return {...f, plans}; })}>
                              {pl.features?.[ft.key] ? '✓' : '✗'}
                            </button>
                          ) : (
                            <input className="pa-input pa-plan-limit" type="number" value={pl.features?.[ft.key] ?? 0}
                              onChange={e => setSettingsForm(f => { const plans=[...(f.plans||[])]; const features={...(plans[idx].features||{})}; features[ft.key]=Number(e.target.value); plans[idx]={...plans[idx], features}; return {...f, plans}; })} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button className="pa-save-btn" onClick={saveSettings}>{T.savePlatformSettings}</button>
            </div>
          </div>
        )}

        {/* ── LANGUAGE ── */}
        {tab === 'language' && settings && (
          <div className="pa-content">
            <div className="pa-settings-form">
              {/* ── DASHBOARD LANGUAGE (what admins see) — separate from public site ── */}
              <div className="lang-section">
                <h3 className="lang-section-title">🖥️ {T.dashboardLanguage}</h3>
                <p className="lang-hint">{T.dashboardLanguageHint}</p>
                <div className="lang-flags-grid">
                  {Object.values(LANGUAGES).map(lang => (
                    <button key={lang.code}
                      className={`lang-flag-btn ${currentCoreLang===lang.code?'active':''}`}
                      disabled={quickLangBusy}
                      onClick={()=>quickSetCoreLang(lang.code)}>
                      <span style={{fontSize:'1.6rem'}}>{lang.flag}</span>
                      <span>{lang.name}</span>
                      {currentCoreLang===lang.code && <span className="lang-check">✓</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{height:1,background:'rgba(255,255,255,0.08)',margin:'.5rem 0 1.5rem'}} />

              <div className="lang-section">
                <h3 className="lang-section-title">🌐 {T.publicSiteLanguage}</h3>
                <p className="lang-hint">{T.publicSiteLanguageHint}</p>
              </div>

              <div className="lang-section">
                <h3 className="lang-section-title">{T.platformUiLangMode}</h3>
                <p className="lang-hint">{T.publicSiteLanguageHint}</p>
                <div className="lang-mode-cards">
                  {[
                    { val:'single', icon:'🔤', title:'Single Language', desc:'Platform UI always shows in one fixed language.' },
                    { val:'multi',  icon:'🌐', title:'Multi-Language',  desc:'Visitors can switch the platform UI language using a flag selector.' },
                  ].map(opt => (
                    <button key={opt.val}
                      className={`lang-mode-card ${settingsForm.languageMode===opt.val?'active':''}`}
                      onClick={()=>setSettingsForm(f=>({...f,languageMode:opt.val}))}>
                      <span className="lang-mode-icon">{opt.icon}</span>
                      <strong>{opt.title}</strong>
                      <span>{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="lang-section">
                <h3 className="lang-section-title">{T.defaultPlatformLang}</h3>
                <div className="lang-flags-grid">
                  {Object.values(LANGUAGES).map(lang => (
                    <button key={lang.code}
                      className={`lang-flag-btn ${settingsForm.defaultLanguage===lang.code?'active':''}`}
                      onClick={()=>setSettingsForm(f=>({...f,defaultLanguage:lang.code}))}>
                      <span style={{fontSize:'1.6rem'}}>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {settingsForm.languageMode === 'multi' && (
                <div className="lang-section">
                  <h3 className="lang-section-title">{T.enabledLangsUiSwitcher}</h3>
                  <p className="lang-hint">{T.enabledLangsHint}</p>
                  <div className="lang-flags-grid">
                    {Object.values(LANGUAGES).map(lang => (
                      <button key={lang.code}
                        className={`lang-flag-btn ${(settingsForm.enabledLanguages||[]).includes(lang.code)?'active':''}`}
                        onClick={()=>togglePlatformLang(lang.code)}>
                        <span style={{fontSize:'1.6rem'}}>{lang.flag}</span>
                        <span>{lang.name}</span>
                        {(settingsForm.enabledLanguages||[]).includes(lang.code) && <span className="lang-check">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview */}
              {settingsForm.languageMode === 'multi' && (settingsForm.enabledLanguages||[]).length > 0 && (
                <div className="lang-section">
                  <h3 className="lang-section-title">{T.switcherPreview}</h3>
                  <div style={{background:'rgba(255,255,255,.04)',borderRadius:12,padding:'1rem',display:'inline-flex',alignItems:'center',gap:'1rem'}}>
                    <span style={{fontSize:'.82rem',color:'rgba(255,255,255,.4)'}}>How it looks on site:</span>
                    <LanguageSwitcher
                      enabled={settingsForm.enabledLanguages||['en']}
                      current={settingsForm.defaultLanguage||'en'}
                      onChange={()=>{}}
                    />
                  </div>
                </div>
              )}

              {/* ── AI quality polish for auto-translated languages ── */}
              {(() => {
                const polishable = [...new Set([
                  settingsForm.defaultLanguage,
                  ...(settingsForm.enabledLanguages || []),
                  currentCoreLang,
                ])].filter(c => c && c !== 'en' && c !== 'fa');
                if (polishable.length === 0) return null;
                return (
                  <div className="lang-section">
                    <h3 className="lang-section-title">✨ {T.polishTitle}</h3>
                    <p className="lang-hint">{T.polishHint}</p>
                    <div className="lang-flags-grid">
                      {polishable.map(code => {
                        const lang = LANGUAGES[code] || { code, name: code, flag: '🏳️' };
                        const busy = !!reviewBusy[code];
                        return (
                          <button key={code} className="lang-flag-btn" disabled={busy}
                            onClick={() => polishLang(code)}>
                            <span style={{fontSize:'1.6rem'}}>{lang.flag}</span>
                            <span>{lang.name}</span>
                            <span style={{fontSize:'.72rem',color:'var(--primary,#c8a96e)',fontWeight:700}}>
                              {busy ? T.polishing : T.polishBtn}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <button className="pa-save-btn" onClick={saveSettings}>{T.saveLanguageSettings}</button>
            </div>
          </div>
        )}

        {/* ── PLATFORM TRANSLATIONS ── */}
        {tab === 'plat-trans' && settings && (
          <div className="pa-content">
            <div className="pa-settings-form">

              <div className="ptrans-banner">
                <span className="ptrans-banner-icon">🌐</span>
                <div>
                  <strong>{T.platformHomepageTranslations}</strong>
                  <p>Translate the public-facing platform UI — buttons, labels, platform name and tagline — for each language visitors can switch to.</p>
                </div>
              </div>

              {/* Language tab strip */}
              <div className="lang-section">
                <h3 className="lang-section-title">{T.translateInto}</h3>
                <div className="ptrans-lang-strip">
                  {Object.values(LANGUAGES).filter(l => l.code !== (settingsForm.defaultLanguage||'en')).map(lang => (
                    <button key={lang.code}
                      className={`ptrans-lang-btn ${platTransLang===lang.code?'active':''}`}
                      onClick={() => setPlatTransLang(lang.code)}>
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
                <div className="ptrans-actions">
                  <button className="ptrans-auto-all-btn" onClick={autoTranslateAllPlatPublic} disabled={!!platAutoTrans._all || platAllLangsBusy}>
                    {platAutoTrans._all
                      ? <><span className="ptrans-spin">⏳</span> {T.tTranslating || 'Translating…'}</>
                      : <><span>✨</span> {T.tAutoAllTo || 'Auto-Translate All to'} {LANGUAGES[platTransLang]?.name}</>}
                  </button>
                  <button className="ptrans-auto-all-btn" onClick={autoTranslateAllPlatLanguages} disabled={platAllLangsBusy || !!platAutoTrans._all}
                    style={{ background:'rgba(16,185,129,.18)', color:'#6ee7b7', borderColor:'rgba(16,185,129,.4)' }}>
                    {platAllLangsBusy
                      ? <><span className="ptrans-spin">⏳</span> {T.tTranslating || 'Translating…'}</>
                      : <><span>🌍</span> {T.tSaveAllLangs || 'Translate All Languages & Save'}</>}
                  </button>
                  {(platUiTrans[platTransLang] || platInfoTrans[platTransLang]) && (
                    <button className="ptrans-clear-btn"
                      onClick={() => {
                        if (window.confirm(T.confirmClearLangTrans || 'Clear all translations for this language?')) {
                          setPlatUiTrans(p => { const n={...p}; delete n[platTransLang]; return n; });
                          setPlatInfoTrans(p => { const n={...p}; delete n[platTransLang]; return n; });
                        }
                      }}>
                      🗑 {T.tClear || 'Clear'} {LANGUAGES[platTransLang]?.flag}
                    </button>
                  )}
                </div>
                <p className="ptrans-method-note">💡 {T.tPlatMethodNote || 'Auto-Translate All to fills the selected language. Translate All Languages & Save translates every enabled language at once and saves immediately. Translation auto-detects the source and uses the AI model plus free providers; review before publishing.'}</p>
              </div>

              {/* Platform info fields */}
              <div className="ptrans-group">
                <div className="ptrans-group-header">🏢 Platform Info</div>
                {[
                  { field:'name',    label:'Platform Name',    src: settingsForm.platformName    || '' },
                  { field:'tagline', label:'Platform Tagline', src: settingsForm.platformTagline || '' },
                ].map(({ field, label, src }) => (
                  <div key={field} className="ptrans-row">
                    <div className="ptrans-source">
                      <span className="ptrans-label">{label}</span>
                      <span className="ptrans-original">{src}</span>
                    </div>
                    <div className="ptrans-input-wrap">
                      <input className="ptrans-input"
                        placeholder={`${label} in ${LANGUAGES[platTransLang]?.name}…`}
                        value={platInfoTrans[platTransLang]?.[field] || ''}
                        onChange={e => setPlatInfoKey(field, e.target.value)} />
                      {src && (
                        <button className="ptrans-sparkle-btn"
                          onClick={() => autoTranslatePlatInfo(field, src)}
                          disabled={!!platAutoTrans[`info-${field}`]}
                          title="Auto-translate">
                          {platAutoTrans[`info-${field}`] ? '⏳' : '✨'}
                        </button>
                      )}
                      {platInfoTrans[platTransLang]?.[field] && (
                        <button className="ptrans-clear-field" onClick={() => setPlatInfoKey(field, '')} title="Clear">×</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* All UI sections */}
              {PLATFORM_LANDING_SECTIONS.map(section => (
                <div key={section.id} className="ptrans-group">
                  <div className="ptrans-group-header">{section.label}</div>
                  {section.keys.map(({ key, default: defaultText }) => (
                    <div key={key} className="ptrans-row ui">
                      <div className="ptrans-source ui">
                        <span className="ptrans-key-badge">{key}</span>
                        <span className="ptrans-default">{defaultText}</span>
                      </div>
                      <div className="ptrans-input-wrap">
                        <input className="ptrans-input"
                          placeholder={defaultText}
                          value={platUiTrans[platTransLang]?.[key] || ''}
                          onChange={e => setPlatUiKey(key, e.target.value)} />
                        <button className="ptrans-sparkle-btn"
                          onClick={() => autoTranslatePlatUi(key, defaultText)}
                          disabled={!!platAutoTrans[`ui-${key}`]}
                          title="Auto-translate">
                          {platAutoTrans[`ui-${key}`] ? '⏳' : '✨'}
                        </button>
                        {platUiTrans[platTransLang]?.[key] && (
                          <button className="ptrans-clear-field" onClick={() => setPlatUiKey(key, '')} title="Clear">×</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              <button className="ptrans-save-btn" onClick={saveSettings}>
                {T.tSavePlatformTrans || 'Save Platform Translations'} — {LANGUAGES[platTransLang]?.flag} {LANGUAGES[platTransLang]?.name}
              </button>
            </div>
          </div>
        )}

        {/* ── ADMIN PANEL LANGUAGE ── */}
        {tab === 'admin-lang' && (
          <div className="pa-content">
            <div className="pa-settings-form">

              <div className="ptrans-banner admin">
                <span className="ptrans-banner-icon">🖥️</span>
                <div>
                  <strong>{T.adminPanelLanguage}</strong>
                  <p>{T.adminPanelLangDesc}</p>
                </div>
              </div>

              <div className="lang-section">
                <h3 className="lang-section-title">{T.chooseLanguage}</h3>
                <div className="lang-flags-grid" style={{marginBottom:'1.25rem'}}>
                  {Object.values(LANGUAGES).map(lang => (
                    <button key={lang.code}
                      className={`lang-flag-btn ${platAdminLang===lang.code?'active':''}`}
                      onClick={() => setPlatAdminLang(lang.code)}>
                      <span style={{fontSize:'1.6rem'}}>{lang.flag}</span>
                      <span>{lang.name}</span>
                      {platAdminLang===lang.code && <span className="lang-check">✓</span>}
                    </button>
                  ))}
                </div>

                <div className="ptrans-actions">
                  {platAdminLang === 'en' ? (
                    <button className="padmin-reset-btn"
                      onClick={() => { setPlatAdminTrans({}); }}>
                      🔄 {T.tResetEnglishDefaults || 'Reset All to English Defaults'}
                    </button>
                  ) : (
                    <button className="ptrans-auto-all-btn" onClick={autoTranslateAllAdmin} disabled={!!adminAutoTrans._all}>
                      {adminAutoTrans._all
                        ? <><span>⏳</span> {T.tTranslating || 'Translating…'}</>
                        : <><span>✨</span> {T.tAutoAllTo || 'Auto-Translate All to'} {LANGUAGES[platAdminLang]?.name}</>}
                    </button>
                  )}
                  {Object.keys(platAdminTrans).length > 0 && platAdminLang !== 'en' && (
                    <button className="ptrans-clear-btn"
                      onClick={() => { if(window.confirm(T.confirmClearAdminTrans || 'Clear all admin panel translations?')) setPlatAdminTrans({}); }}>
                      🗑 {T.tClearAll || 'Clear All'}
                    </button>
                  )}
                </div>

                {/* Live preview */}
                <div className="padmin-preview">
                  <span className="padmin-preview-label">Preview:</span>
                  {['adm_overview','adm_orders','adm_products','adm_customers','adm_settings','adm_language'].map(k => (
                    <span key={k} className="padmin-preview-chip">
                      {platAdminTrans[k] || ADMIN_UI_DEFAULTS[k]}
                    </span>
                  ))}
                </div>
              </div>

              {ADMIN_UI_SECTIONS.map(section => (
                <div key={section.id} className="ptrans-group">
                  <div className="ptrans-group-header">{section.label}</div>
                  {section.keys.map(({ key, default: defaultText }) => (
                    <div key={key} className="ptrans-row ui">
                      <div className="ptrans-source ui">
                        <span className="ptrans-key-badge">{key}</span>
                        <span className="ptrans-default">{defaultText}</span>
                      </div>
                      <div className="ptrans-input-wrap">
                        <input className="ptrans-input"
                          placeholder={platAdminLang === 'en' ? '(uses English default)' : defaultText}
                          value={platAdminTrans[key] || ''}
                          onChange={e => setAdminKey(key, e.target.value)}
                          readOnly={platAdminLang === 'en'}
                          style={platAdminLang === 'en' ? {opacity:.4,cursor:'not-allowed'} : {}} />
                        {platAdminLang !== 'en' && (
                          <button className="ptrans-sparkle-btn"
                            onClick={() => autoTranslateAdminKey(key, defaultText)}
                            disabled={!!adminAutoTrans[key]}
                            title="Auto-translate">
                            {adminAutoTrans[key] ? '⏳' : '✨'}
                          </button>
                        )}
                        {platAdminTrans[key] && (
                          <button className="ptrans-clear-field" onClick={() => setAdminKey(key,'')} title="Clear">×</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              <button className="ptrans-save-btn admin" onClick={saveSettings}>
                Save — {LANGUAGES[platAdminLang]?.flag} {LANGUAGES[platAdminLang]?.name || 'English'} Admin Panel
              </button>
            </div>
          </div>
        )}

        {/* ── VISITORS (humans vs bots, platform + per tenant) ── */}
        {tab === 'visitors' && (
          <div className="pa-content">
            <div style={{marginBottom:'1.25rem'}}>
              <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:'1.4rem',color:'#fff',marginBottom:'.25rem'}}>👣 {T.visitors || 'Visitors'}</h1>
              <p style={{fontSize:'.8rem',color:'rgba(255,255,255,.35)'}}>{T.visitorsDesc || 'Real traffic to the platform and every tenant, with bots separated from real people.'}</p>
            </div>
            <VisitorsPanel accent="#6366f1" showTenants fetchStats={(d) => platformApi.getVisitors(d)} />
          </div>
        )}

        {/* ── SEO & GOOGLE (graphical editor for the SEO/root admin) ── */}
        {tab === 'seo' && (
          <div className="pa-content">
            <div style={{marginBottom:'1.25rem'}}>
              <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:'1.4rem',color:'#fff',marginBottom:'.25rem'}}>🔍 {T.seo || 'SEO & Google'}</h1>
              <p style={{fontSize:'.8rem',color:'rgba(255,255,255,.35)'}}>{T.seoDesc || 'Connect Google Analytics, Tag Manager and Search Console, and reach the SEO files — no code needed.'}</p>
            </div>
            <div className="pa-settings-grid">
              <div className="pa-field">
                <label>{T.seoGa || 'Google Analytics ID (GA4)'}</label>
                <input className="pa-input" value={settingsForm.seo?.googleAnalyticsId ?? ''} placeholder="G-XXXXXXXXXX"
                  onChange={e=>setSeo('googleAnalyticsId', e.target.value.trim())} />
                <span className="pa-toggle-hint">{T.seoGaHint || 'From Google Analytics → Admin → Data Streams. Tracks all pages automatically.'}</span>
              </div>
              <div className="pa-field">
                <label>{T.seoGtm || 'Google Tag Manager ID'}</label>
                <input className="pa-input" value={settingsForm.seo?.googleTagManagerId ?? ''} placeholder="GTM-XXXXXXX"
                  onChange={e=>setSeo('googleTagManagerId', e.target.value.trim())} />
              </div>
              <div className="pa-field">
                <label>{T.seoGsc || 'Google Search Console verification'}</label>
                <input className="pa-input" value={settingsForm.seo?.googleSiteVerification ?? ''} placeholder="google-site-verification token"
                  onChange={e=>setSeo('googleSiteVerification', e.target.value.trim())} />
                <span className="pa-toggle-hint">{T.seoGscHint || 'The content value of the meta tag Search Console gives you (HTML-tag method).'}</span>
              </div>
              <div className="pa-field">
                <label>{T.seoBing || 'Bing verification'}</label>
                <input className="pa-input" value={settingsForm.seo?.bingSiteVerification ?? ''} placeholder="msvalidate.01 token"
                  onChange={e=>setSeo('bingSiteVerification', e.target.value.trim())} />
              </div>
            </div>
            <div style={{display:'flex',gap:'.6rem',alignItems:'center',marginTop:'1rem',flexWrap:'wrap'}}>
              <button className="pa-save-btn" disabled={seoSaving} onClick={saveSeo}>
                {seoSaving ? `⏳ ${T.saving || 'Saving…'}` : `💾 ${T.save || 'Save'}`}
              </button>
              {seoSaved && <span style={{color:'#6ee7b7',fontSize:'.85rem'}}>✓ {T.saved || 'Saved'}</span>}
            </div>
            {/* ── Live crawler files: view + edit robots.txt / llms.txt ── */}
            <div style={{marginTop:'2rem',borderTop:'1px solid rgba(255,255,255,.08)',paddingTop:'1.25rem'}}>
              <h2 style={{fontSize:'1.05rem',color:'#fff',marginBottom:'.25rem'}}>📄 {T.seoFiles || 'Crawler files (robots.txt & llms.txt)'}</h2>
              <p style={{fontSize:'.8rem',color:'rgba(255,255,255,.35)',marginBottom:'1rem'}}>{T.seoFilesHint || 'See and edit exactly what search engines and AI assistants read. Leave the auto-generated version, or type your own and Save to override it. Reset returns to auto.'}</p>
              {filesLoading && <p style={{fontSize:'.8rem',color:'rgba(255,255,255,.4)'}}>⏳ {T.loading || 'Loading…'}</p>}
              {[
                ['robots', 'robots.txt', '/robots.txt', robotsDraft, setRobotsDraft],
                ['llms',   'llms.txt',   '/llms.txt',   llmsDraft,   setLlmsDraft],
              ].map(([which, lbl, href, val, setVal]) => {
                const isOverride = !!(seoFiles && seoFiles[which] && seoFiles[which].override && seoFiles[which].override.trim());
                return (
                  <div key={which} style={{marginBottom:'1.5rem'}}>
                    <div style={{display:'flex',alignItems:'center',gap:'.6rem',marginBottom:'.4rem',flexWrap:'wrap'}}>
                      <strong style={{color:'#fff',fontSize:'.9rem'}}>{lbl}</strong>
                      <span style={{fontSize:'.7rem',fontWeight:700,padding:'.15rem .5rem',borderRadius:6,background:isOverride?'rgba(245,158,11,.18)':'rgba(16,185,129,.18)',color:isOverride?'#fcd34d':'#6ee7b7'}}>
                        {isOverride ? (T.seoFileCustom || 'Custom override') : (T.seoFileAuto || 'Auto-generated')}
                      </span>
                      <a href={href} target="_blank" rel="noreferrer" style={{marginLeft:'auto',color:'#a5b4fc',fontSize:'.78rem',fontWeight:700,textDecoration:'none'}}>🔗 {T.seoFileOpenLive || 'Open live'}</a>
                    </div>
                    <textarea
                      className="pa-input"
                      value={val}
                      onChange={e => setVal(e.target.value)}
                      spellCheck={false}
                      rows={which === 'robots' ? 8 : 12}
                      style={{width:'100%',fontFamily:'monospace',fontSize:'.78rem',lineHeight:1.5,resize:'vertical',whiteSpace:'pre'}}
                    />
                    <div style={{display:'flex',gap:'.6rem',alignItems:'center',marginTop:'.5rem',flexWrap:'wrap'}}>
                      <button className="pa-save-btn" disabled={fileSaving === which} onClick={() => saveSeoFile(which)}>
                        {fileSaving === which ? `⏳ ${T.saving || 'Saving…'}` : `💾 ${T.save || 'Save'}`}
                      </button>
                      <button
                        onClick={() => resetSeoFile(which)}
                        disabled={fileSaving === which || !isOverride}
                        style={{padding:'.5rem .9rem',borderRadius:8,border:'1px solid rgba(255,255,255,.14)',background:'transparent',color:isOverride?'#fca5a5':'rgba(255,255,255,.3)',fontSize:'.82rem',fontWeight:700,cursor:isOverride?'pointer':'not-allowed'}}>
                        ↺ {T.seoFileReset || 'Reset to auto'}
                      </button>
                      {fileSaved === which && <span style={{color:'#6ee7b7',fontSize:'.85rem'}}>✓ {T.saved || 'Saved'}</span>}
                    </div>
                  </div>
                );
              })}
              <div style={{display:'flex',gap:'.6rem',flexWrap:'wrap',marginTop:'.5rem'}}>
                <a href="/sitemap.xml" target="_blank" rel="noreferrer" style={{padding:'.5rem .9rem',borderRadius:8,border:'1px solid rgba(255,255,255,.14)',color:'#a5b4fc',fontSize:'.82rem',fontWeight:700,textDecoration:'none'}}>🔗 sitemap.xml</a>
                <button onClick={() => setTab('blog')} style={{padding:'.5rem .9rem',borderRadius:8,border:'1px solid rgba(255,255,255,.14)',background:'transparent',color:'#a5b4fc',fontSize:'.82rem',fontWeight:700,cursor:'pointer'}}>📝 {T.blog || 'Blog'}</button>
              </div>
            </div>
          </div>
        )}

        {/* ── ANALYTICS & INSIGHTS ── */}
        {tab === 'blog' && (
          <div className="pa-content">
            <div style={{marginBottom:'1.25rem'}}>
              <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:'1.4rem',color:'#fff',marginBottom:'.25rem'}}>📝 {T.blog || 'Blog'}</h1>
              <p style={{fontSize:'.8rem',color:'rgba(255,255,255,.35)'}}>{T.blogManageHint || 'Generate, edit and translate your SEO blog posts. Editing re-translates into every enabled language.'}</p>
            </div>
            <BlogManager
              accent={settingsForm.primaryColor || '#c8a96e'}
              api={{ list: platformApi.listPosts, generate: platformApi.generatePost, create: platformApi.createPost, edit: platformApi.editPost, remove: platformApi.deletePost }}
            />
          </div>
        )}

        {tab === 'analytics' && (
          <div className="pa-content">
            <div style={{marginBottom:'1.25rem'}}>
              <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:'1.4rem',color:'#fff',marginBottom:'.25rem'}}>{T.analyticsTitle}</h1>
              <p style={{fontSize:'.8rem',color:'rgba(255,255,255,.35)'}}>{T.analyticsDesc}</p>
            </div>
            <AnalyticsPanel
              scope="platform"
              currencyCode="USD"
              fetchData={async (days) => platformApi.getPlatformAnalytics(days)}
              fetchLogs={async (params) => platformApi.getPlatformAudit(params)}
              aiAnalyze={async (messages) => {
                const res = await fetch(`${API}/ai-analyze`, {
                  method:'POST',
                  headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('nooshin_platform_token')},
                  body: JSON.stringify({ messages, scope:'platform' })
                });
                return res.json();
              }}
              exportAuditUrl={(params) => `${API}/platform/audit/export.csv?${params}`}
            />
          </div>
        )}
      </main>

      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        .pa-root{display:flex;min-height:100vh;background:#080810;color:#f0eeff;font-family:var(--core-font,'DM Sans',sans-serif)}
        .pa-notif{position:fixed;top:1.5rem;right:1.5rem;z-index:9999;padding:.85rem 1.5rem;border-radius:12px;font-weight:600;font-size:.9rem;animation:slideIn .3s ease}
        .pa-notif.success{background:#1e1b4b;color:#a5b4fc;border:1px solid #6366f1}
        .pa-notif.error{background:#7f1d1d;color:#fca5a5;border:1px solid #ef4444}
        @keyframes slideIn{from{transform:translateX(30px);opacity:0}to{transform:none;opacity:1}}

        .pa-sidebar{width:240px;background:#0d0d1a;border-right:1px solid rgba(99,102,241,.15);display:flex;flex-direction:column;position:fixed;top:0;left:0;height:100vh;z-index:40}[dir='rtl'] .pa-sidebar{left:auto;right:0;border-right:none;border-left:1px solid rgba(99,102,241,.15)}[dir='rtl'] .pa-main{margin-left:0;margin-right:240px}
        .pa-brand{display:flex;align-items:center;gap:.75rem;padding:1.5rem;border-bottom:1px solid rgba(99,102,241,.12)}
        .pa-brand-name{font-weight:700;color:#fff;font-size:.95rem;font-family:var(--core-font,'DM Sans',sans-serif)}
        .pa-brand-role{font-size:.65rem;color:rgba(255,255,255,.35);text-transform:uppercase;letter-spacing:.1em}
        .pa-nav{flex:1;padding:1rem;display:flex;flex-direction:column;gap:.2rem}
        .pa-nav-btn{display:flex;align-items:center;gap:.75rem;padding:.75rem 1rem;border:none;background:none;color:rgba(255,255,255,.45);cursor:pointer;border-radius:10px;font-family:var(--core-font,'DM Sans',sans-serif);font-size:.9rem;text-align:start;transition:all .2s}
        .pa-nav-btn:hover{background:rgba(99,102,241,.1);color:#fff}
        .pa-nav-btn.active{background:rgba(99,102,241,.25);color:#a5b4fc;font-weight:600;border:1px solid rgba(99,102,241,.3)}
        .pa-sidebar-foot{padding:1rem;border-top:1px solid rgba(99,102,241,.12)}
        .pa-admin-info{margin-bottom:.75rem}
        .pa-admin-name{font-size:.85rem;font-weight:600;color:#fff}
        .pa-admin-email{font-size:.72rem;color:rgba(255,255,255,.3)}
        .pa-logout{width:100%;padding:.65rem;background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.2);border-radius:10px;color:#f87171;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.85rem;transition:all .2s}
        .pa-logout:hover{background:rgba(239,68,68,.2)}

        .pa-main{margin-left:240px;flex:1;display:flex;flex-direction:column;min-height:100vh}
        .pa-topbar{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 2rem;border-bottom:1px solid rgba(99,102,241,.1);background:#0d0d1a;position:sticky;top:0;z-index:30}
        .pa-topbar-title{font-family:'Playfair Display',serif;font-size:1.4rem;color:#fff}
        .pa-topbar-right{display:flex;align-items:center;gap:.75rem;flex-wrap:wrap}
        .pa-tb-group{display:flex;align-items:center;gap:.3rem;background:rgba(255,255,255,.05);border:1px solid rgba(99,102,241,.25);border-radius:9px;padding:.2rem .5rem;font-size:.85rem}
        .pa-tb-select{background:none;border:none;color:#fff;font-family:'DM Sans',sans-serif;font-size:.8rem;cursor:pointer;outline:none;max-width:150px}
        .pa-tb-select option{background:#15152a;color:#fff}
        .pa-content{padding:2rem}

        .pa-stats{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:1rem;margin-bottom:2rem}
        .pa-stat-card{background:rgba(99,102,241,.08);border:1px solid rgba(99,102,241,.2);border-radius:14px;padding:1.25rem}
        .pa-stat-icon{font-size:1.4rem;margin-bottom:.6rem}
        .pa-stat-val{font-size:1.75rem;font-weight:700;color:#fff}
        .pa-stat-label{font-size:.72rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.05em;margin-top:.2rem}

        .pa-card{background:rgba(255,255,255,.03);border:1px solid rgba(99,102,241,.15);border-radius:14px;padding:1.5rem}
        .pa-card h3{font-family:'Playfair Display',serif;color:#fff;margin-bottom:1rem;font-size:1rem}
        .pa-quick{display:block;width:100%;padding:.7rem 1rem;margin-bottom:.5rem;background:rgba(99,102,241,.08);border:1px solid rgba(99,102,241,.2);border-radius:10px;color:rgba(255,255,255,.7);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.875rem;text-align:left;transition:all .2s}
        .pa-quick:hover{background:rgba(99,102,241,.2);color:#fff}

        .pa-tab-bar{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.5rem}
        .pa-count{color:rgba(255,255,255,.4);font-size:.875rem}
        .pa-add-btn{padding:.65rem 1.4rem;background:var(--pa);border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.875rem;font-weight:700;cursor:pointer;transition:opacity .2s}
        .pa-add-btn:hover{opacity:.85}
        .pa-empty{text-align:center;padding:4rem;color:rgba(255,255,255,.25)}
        .pa-list{display:flex;flex-direction:column;gap:.75rem}

        .pa-rest-row,.pa-owner-row,.pa-admin-row{display:flex;align-items:center;gap:1rem;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.06);border-radius:14px;padding:1rem 1.25rem;flex-wrap:wrap}
        .pa-rest-info{flex:1;min-width:0}
        .pa-rest-name{font-weight:600;color:#fff;font-size:.9rem;margin-bottom:.3rem}
        .pa-rest-meta{display:flex;gap:.75rem;flex-wrap:wrap;font-size:.75rem;color:rgba(255,255,255,.4)}
        .pa-badge{background:rgba(99,102,241,.2);color:#a5b4fc;padding:.15rem .5rem;border-radius:6px;font-size:.7rem;font-family:monospace}
        .pa-lang-badge{font-size:.75rem;color:rgba(255,255,255,.5);background:rgba(255,255,255,.05);padding:.2rem .6rem;border-radius:8px}
        .pa-active-badge{padding:.3rem .85rem;border-radius:12px;border:none;cursor:pointer;font-size:.72rem;font-weight:700;white-space:nowrap;transition:all .2s}
        .pa-active-badge.on{background:rgba(16,185,129,.15);color:#6ee7b7}
        .pa-active-badge.off{background:rgba(239,68,68,.15);color:#fca5a5}
        .pa-row-actions{display:flex;gap:.4rem}
        .pa-icon-btn{width:34px;height:34px;border:1px solid rgba(255,255,255,.1);border-radius:8px;background:rgba(255,255,255,.04);color:rgba(255,255,255,.6);cursor:pointer;font-size:.9rem;display:flex;align-items:center;justify-content:center;transition:all .2s}
        .pa-icon-btn:hover{background:rgba(255,255,255,.1)}
        .pa-icon-btn.danger:hover{background:rgba(239,68,68,.15);border-color:rgba(239,68,68,.3);color:#f87171}
        .pa-plan-select{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);border-radius:8px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.8rem;padding:.4rem .75rem;cursor:pointer;outline:none}
        .pa-role-badge{padding:.15rem .5rem;border-radius:6px;font-size:.68rem;font-weight:700}
        .pa-role-badge.root{background:rgba(234,179,8,.2);color:#fde047}
        .pa-role-badge.admin{background:rgba(99,102,241,.2);color:#a5b4fc}
        .pa-role-badge.seo{background:rgba(16,185,129,.2);color:#6ee7b7}
        .pa-perms{display:flex;gap:.3rem;flex-wrap:wrap}
        .pa-perm-tag{padding:.15rem .45rem;border-radius:6px;font-size:.65rem;font-weight:600}
        .pa-perm-tag.on{background:rgba(16,185,129,.15);color:#6ee7b7}
        .pa-perm-tag.off{background:rgba(255,255,255,.05);color:rgba(255,255,255,.25)}

        .pa-settings-form{background:rgba(255,255,255,.03);border:1px solid rgba(99,102,241,.15);border-radius:16px;padding:2rem}
        .pa-settings-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1.5rem}
        .pa-field{display:flex;flex-direction:column;gap:.4rem}
        .pa-field.full{grid-column:1/-1}
        .pa-field label{font-size:.72rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.08em}
        .pa-input{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:10px;padding:.75rem 1rem;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;outline:none;transition:border-color .2s;width:100%}
        .pa-input:focus{border-color:var(--pa)}
        .pa-toggle-row{display:flex;align-items:center;gap:.75rem}
                .pa-plans-editor{margin-top:1.75rem;border-top:1px solid rgba(255,255,255,.08);padding-top:1.5rem}
        .pa-plans-h3{color:#fff;font-size:1rem;margin-bottom:.3rem}
        .pa-plan-edit{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:1rem;margin-top:1rem}
        .pa-plan-edit-head{display:flex;flex-wrap:wrap;align-items:center;gap:.6rem;margin-bottom:.85rem}
        .pa-plan-name{font-weight:700;max-width:160px}
        .pa-plan-price-wrap{display:flex;align-items:center;gap:.3rem}
        .pa-plan-price-in{width:80px}.pa-plan-cur{width:64px}.pa-plan-limit{width:72px}
        .pa-plan-int{color:rgba(255,255,255,.4);font-size:.8rem}
        .pa-plan-flag{display:flex;align-items:center;gap:.3rem;font-size:.78rem;color:rgba(255,255,255,.6)}
        .pa-plan-feats{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:.5rem}
        .pa-plan-feat{display:flex;align-items:center;justify-content:space-between;gap:.5rem;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.06);border-radius:8px;padding:.35rem .6rem}
        .pa-plan-feat-lbl{font-size:.74rem;color:rgba(255,255,255,.6)}
        .pa-toggle.sm{padding:.2rem .55rem;font-size:.72rem;border-radius:8px}
        .pa-toggle.on{background:var(--pa-accent,#c8a96e);border-color:var(--pa-accent,#c8a96e);color:#1a1a1a}
        .pa-toggle{padding:.5rem 1.1rem;border-radius:20px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.05);color:rgba(255,255,255,.5);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.82rem;font-weight:600;transition:all .2s}
        .pa-toggle.on{background:rgba(16,185,129,.15);border-color:rgba(16,185,129,.3);color:#6ee7b7}
        .pa-toggle-hint{font-size:.75rem;color:rgba(255,255,255,.3)}
        .pa-localize-status{display:flex;flex-direction:column;gap:.5rem;margin-top:.6rem}
        .pa-localize-bar{height:8px;border-radius:6px;background:rgba(255,255,255,.08);overflow:hidden}
        .pa-localize-bar-fill{height:100%;background:linear-gradient(90deg,#c8a96e,#e0c48a);transition:width .4s ease}
        .pa-save-btn{padding:.9rem 2rem;background:var(--pa);border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.95rem;font-weight:700;cursor:pointer;transition:opacity .2s}
        .pa-save-btn:hover{opacity:.85}

        /* Language shared styles */
        .lang-section{margin-bottom:2rem}
        .lang-section-title{font-family:'Playfair Display',serif;color:#fff;font-size:1rem;margin-bottom:.75rem}
        .lang-hint{font-size:.8rem;color:rgba(255,255,255,.4);margin-bottom:1rem}
        .lang-mode-cards{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
        .lang-mode-card{display:flex;flex-direction:column;gap:.4rem;padding:1.25rem;background:rgba(255,255,255,.03);border:2px solid rgba(255,255,255,.08);border-radius:14px;cursor:pointer;text-align:left;color:rgba(255,255,255,.55);font-family:'DM Sans',sans-serif;transition:all .2s}
        .lang-mode-card:hover{border-color:rgba(99,102,241,.3)}
        .lang-mode-card.active{border-color:var(--pa);background:rgba(99,102,241,.08);color:#fff}
        .lang-mode-icon{font-size:2rem}
        .lang-mode-card strong{color:#fff;font-size:.95rem}
        .lang-mode-card span:last-child{font-size:.78rem;color:rgba(255,255,255,.4);line-height:1.5}
        .lang-flags-grid{display:flex;flex-wrap:wrap;gap:.5rem}
        .lang-flag-btn{display:flex;flex-direction:column;align-items:center;gap:.3rem;padding:.65rem .9rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:12px;cursor:pointer;color:rgba(255,255,255,.55);font-family:'DM Sans',sans-serif;font-size:.72rem;min-width:72px;transition:all .2s;position:relative}
        .lang-flag-btn:hover{border-color:rgba(99,102,241,.3);color:#fff}
        .lang-flag-btn.active{border-color:var(--pa);background:rgba(99,102,241,.12);color:#fff}
        .lang-check{position:absolute;top:.25rem;right:.35rem;font-size:.6rem;color:var(--pa)}

        /* ── Platform Translations (ptrans) styles ─────────────────────────── */
        .ptrans-banner{display:flex;align-items:flex-start;gap:1rem;background:rgba(99,102,241,.07);border:1px solid rgba(99,102,241,.2);border-radius:14px;padding:1.25rem 1.5rem;margin-bottom:2rem}
        .ptrans-banner.admin{background:rgba(16,185,129,.05);border-color:rgba(16,185,129,.18)}
        .ptrans-banner-icon{font-size:2rem;flex-shrink:0;margin-top:.1rem}
        .ptrans-banner strong{display:block;color:#fff;font-size:.95rem;margin-bottom:.3rem}
        .ptrans-banner p{font-size:.8rem;color:rgba(255,255,255,.38);line-height:1.65;margin:0}

        .ptrans-lang-strip{display:flex;flex-wrap:wrap;gap:.5rem;margin-bottom:1rem}
        .ptrans-lang-btn{display:flex;align-items:center;gap:.45rem;padding:.45rem 1rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:20px;color:rgba(255,255,255,.5);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.83rem;transition:all .2s}
        .ptrans-lang-btn:hover{background:rgba(99,102,241,.1);border-color:rgba(99,102,241,.3);color:#fff}
        .ptrans-lang-btn.active{background:var(--pa);border-color:var(--pa);color:#fff;font-weight:700;box-shadow:0 0 16px rgba(99,102,241,.4)}

        .ptrans-actions{display:flex;gap:.75rem;flex-wrap:wrap;margin-bottom:.75rem}
        .ptrans-method-note{font-size:.78rem;line-height:1.6;color:rgba(255,255,255,.4);background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:.75rem 1rem;margin:0 0 1.25rem}
        .ptrans-auto-all-btn{display:inline-flex;align-items:center;gap:.5rem;padding:.6rem 1.35rem;background:linear-gradient(135deg,#7c3aed,#4f46e5);border:none;border-radius:22px;color:#fff;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.85rem;font-weight:700;transition:opacity .2s;box-shadow:0 4px 14px rgba(79,70,229,.35)}
        .ptrans-auto-all-btn:hover:not(:disabled){opacity:.88}
        .ptrans-auto-all-btn:disabled{opacity:.45;cursor:not-allowed}
        .ptrans-clear-btn{display:inline-flex;align-items:center;gap:.4rem;padding:.6rem 1.1rem;background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.22);border-radius:22px;color:#fca5a5;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.83rem;font-weight:600;transition:all .2s}
        .ptrans-clear-btn:hover{background:rgba(239,68,68,.16);border-color:rgba(239,68,68,.4)}
        .padmin-reset-btn{display:inline-flex;align-items:center;gap:.5rem;padding:.6rem 1.35rem;background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.28);border-radius:22px;color:#6ee7b7;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.85rem;font-weight:700;transition:all .2s}
        .padmin-reset-btn:hover{background:rgba(16,185,129,.2);border-color:rgba(16,185,129,.5)}

        .padmin-preview{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:12px;padding:.8rem 1.1rem}
        .padmin-preview-label{font-size:.68rem;color:rgba(255,255,255,.28);text-transform:uppercase;letter-spacing:.09em;margin-right:.2rem;white-space:nowrap}
        .padmin-preview-chip{padding:.22rem .65rem;background:rgba(99,102,241,.14);border:1px solid rgba(99,102,241,.24);border-radius:8px;color:#a5b4fc;font-size:.78rem;font-weight:600}

        .ptrans-group{background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.07);border-radius:14px;padding:0;margin-bottom:.875rem;overflow:hidden}
        .ptrans-group-header{font-size:.73rem;font-weight:700;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.09em;padding:.75rem 1.1rem;background:rgba(255,255,255,.03);border-bottom:1px solid rgba(255,255,255,.06)}
        .ptrans-row{display:grid;grid-template-columns:200px 1fr;gap:1rem;align-items:center;padding:.65rem 1.1rem;border-bottom:1px solid rgba(255,255,255,.04)}
        .ptrans-row:last-child{border-bottom:none}
        .ptrans-row.ui{grid-template-columns:220px 1fr}
        .ptrans-source{display:flex;flex-direction:column;gap:.2rem}
        .ptrans-source.ui{}
        .ptrans-label{font-size:.85rem;color:rgba(255,255,255,.7);font-weight:600}
        .ptrans-original{font-size:.75rem;color:rgba(255,255,255,.32);font-style:italic}
        .ptrans-key-badge{font-size:.62rem;color:rgba(99,102,241,.7);background:rgba(99,102,241,.1);border:1px solid rgba(99,102,241,.2);border-radius:5px;padding:.1rem .4rem;font-family:monospace;width:fit-content}
        .ptrans-default{font-size:.8rem;color:rgba(255,255,255,.55);margin-top:.1rem}
        .ptrans-input-wrap{display:flex;gap:.35rem;align-items:center}
        .ptrans-input{flex:1;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:8px;padding:.55rem .8rem;color:#fff;font-family:'DM Sans',sans-serif;font-size:.85rem;outline:none;transition:border-color .2s;min-width:0}
        .ptrans-input:focus{border-color:var(--pa)}
        .ptrans-sparkle-btn{flex-shrink:0;width:34px;height:34px;border:1px solid rgba(124,58,237,.35);border-radius:8px;background:rgba(124,58,237,.1);color:#a78bfa;cursor:pointer;font-size:1rem;display:flex;align-items:center;justify-content:center;transition:all .2s}
        .ptrans-sparkle-btn:hover:not(:disabled){background:rgba(124,58,237,.22);border-color:rgba(124,58,237,.6)}
        .ptrans-sparkle-btn:disabled{opacity:.4;cursor:not-allowed}
        .ptrans-clear-field{flex-shrink:0;width:28px;height:34px;border:1px solid rgba(255,255,255,.08);border-radius:6px;background:transparent;color:rgba(255,255,255,.3);cursor:pointer;font-size:1.1rem;display:flex;align-items:center;justify-content:center;transition:all .2s;line-height:1}
        .ptrans-clear-field:hover{background:rgba(239,68,68,.1);border-color:rgba(239,68,68,.3);color:#fca5a5}
        .ptrans-save-btn{display:flex;align-items:center;justify-content:center;width:100%;margin-top:1.75rem;padding:1rem 2rem;background:var(--pa);border:none;border-radius:12px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.95rem;font-weight:700;cursor:pointer;transition:opacity .2s;box-shadow:0 4px 18px rgba(99,102,241,.3)}
        .ptrans-save-btn:hover{opacity:.88}
        .ptrans-save-btn.admin{background:linear-gradient(135deg,#059669,#047857)}

        @media(max-width:768px){.pa-sidebar{display:none}.pa-main{margin-left:0}.pa-settings-grid{grid-template-columns:1fr}.ptrans-row,.ptrans-row.ui{grid-template-columns:1fr}.ptrans-source{margin-bottom:.35rem}}

        /* ── Core Settings ─────────────────────────────────────────── */
        .core-banner{display:flex;align-items:flex-start;gap:1rem;background:rgba(234,179,8,.07);border:1px solid rgba(234,179,8,.2);border-radius:14px;padding:1.25rem 1.5rem;margin-bottom:2rem}
        .core-banner-icon{font-size:2rem;flex-shrink:0}
        .core-banner strong{display:block;color:#fff;font-size:.95rem;margin-bottom:.3rem}
        .core-banner p{font-size:.8rem;color:rgba(255,255,255,.4);line-height:1.65;margin:0}
        .core-mode-section{margin-bottom:2rem}
        .core-mode-grid{display:grid;grid-template-columns:1fr 1fr;gap:1.25rem}
        .core-mode-card{position:relative;display:flex;flex-direction:column;gap:.6rem;padding:1.5rem;background:rgba(255,255,255,.03);border:2px solid rgba(255,255,255,.08);border-radius:16px;cursor:pointer;text-align:left;font-family:'DM Sans',sans-serif;transition:all .25s;color:rgba(255,255,255,.55)}
        .core-mode-card:hover{border-color:rgba(99,102,241,.35);background:rgba(99,102,241,.06)}
        .core-mode-card.active{border-color:#6366f1;background:rgba(99,102,241,.1);color:#fff;box-shadow:0 0 30px rgba(99,102,241,.15)}
        .core-mode-card.active.persian{border-color:#10b981;background:rgba(16,185,129,.08);box-shadow:0 0 30px rgba(16,185,129,.12)}
        .core-mode-top{display:flex;align-items:center;justify-content:space-between}
        .core-mode-flag{font-size:2.5rem}
        .core-mode-badge{font-size:.7rem;font-weight:700;padding:.25rem .75rem;border-radius:20px;background:rgba(99,102,241,.15);color:#a5b4fc;border:1px solid rgba(99,102,241,.25)}
        .core-mode-badge.persian{background:rgba(16,185,129,.15);color:#6ee7b7;border-color:rgba(16,185,129,.3)}
        .core-mode-badge.active{background:#6366f1;color:#fff}
        .core-mode-badge.persian.active{background:#10b981;color:#fff}
        .core-mode-title{font-size:1.05rem;color:#fff;font-weight:700}
        .core-mode-desc{font-size:.8rem;color:rgba(255,255,255,.45);line-height:1.6}
        .core-mode-preview{display:flex;gap:.4rem;flex-wrap:wrap;margin-top:.3rem}
        .core-mode-preview span{padding:.2rem .6rem;border-radius:8px;font-size:.7rem;background:rgba(255,255,255,.06);color:rgba(255,255,255,.4);border:1px solid rgba(255,255,255,.08)}
        .core-mode-check{position:absolute;top:.9rem;right:.9rem;width:24px;height:24px;border-radius:50%;background:#6366f1;color:#fff;font-size:.75rem;display:flex;align-items:center;justify-content:center;font-weight:700}
        .core-mode-check.persian{background:#10b981}
        .core-current{display:flex;align-items:center;gap:.75rem;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:12px;padding:.85rem 1.25rem;margin-bottom:1rem}
        .core-current-label{font-size:.8rem;color:rgba(255,255,255,.4)}
        .core-current-val{font-size:.85rem;font-weight:700;padding:.25rem .85rem;border-radius:20px}
        .core-current-val.global{background:rgba(99,102,241,.15);color:#a5b4fc;border:1px solid rgba(99,102,241,.25)}
        .core-current-val.persian{background:rgba(16,185,129,.15);color:#6ee7b7;border:1px solid rgba(16,185,129,.25)}
        .core-warning{display:flex;align-items:flex-start;gap:.6rem;background:rgba(234,179,8,.06);border:1px solid rgba(234,179,8,.18);border-radius:10px;padding:.85rem 1.1rem;margin-bottom:1.5rem;font-size:.8rem;color:rgba(255,255,255,.55)}
        .core-warning strong{color:#fde047;display:block;margin-bottom:.15rem}
        .core-save-btn{width:100%;padding:1rem;background:linear-gradient(135deg,#6366f1,#4f46e5);border:none;border-radius:12px;color:#fff;font-size:.95rem;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;transition:opacity .2s;box-shadow:0 4px 18px rgba(99,102,241,.3)}
        .core-save-btn:hover:not(:disabled){opacity:.88}
        .core-save-btn:disabled{opacity:.45;cursor:not-allowed}
        @media(max-width:600px){.core-mode-grid{grid-template-columns:1fr}}
      `}</style>
    </div>
  );
}

// ── Admin Create/Edit Modal ───────────────────────────────────────────────────
function AdminModal({ modal, onSave, onClose }) {
  const T = useCoreT();
  const isEdit = modal.mode === 'edit';
  const a = modal.data;
  const [form, setForm] = useState({
    name:     a?.name  || '',
    email:    a?.email || '',
    phone:    a?.phone || '',
    password: '',
    role:     a?.role === 'seo' ? 'seo' : 'admin',
    permissions: a?.permissions || { restaurants:true, owners:true, customers:false, admins:false, settings:false },
  });

  const toggle = (p) => setForm(f => ({ ...f, permissions: { ...f.permissions, [p]: !f.permissions[p] } }));

  const handle = async () => {
    if (!form.name || (!isEdit && (!form.email || !form.password))) {
      alert(T.nameRequired); return;
    }
    const payload = { name: form.name, phone: form.phone, role: form.role, permissions: form.permissions };
    if (!isEdit) { payload.email = form.email; payload.password = form.password; }
    if (form.password && isEdit) payload.password = form.password;
    await onSave(payload);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{'--pri':'#6366f1'}}>
        <div className="modal-header">
          <h2>{isEdit ? T.editAdmin : T.createAdmin}</h2>
          <button className="modal-x" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="modal-grid">
            <div className="mf full"><label>{T.name} *</label>
              <input className="mi" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Admin name" autoFocus /></div>
            {!isEdit && <>
              <div className="mf"><label>{T.email} *</label>
                <input className="mi" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="admin@nowshin.ir" /></div>
              <div className="mf"><label>{T.password} *</label>
                <input className="mi" type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="min 6 chars" /></div>
            </>}
            {isEdit && <div className="mf full"><label>New Password <span style={{color:'rgba(255,255,255,.3)',fontWeight:400}}>{T.leaveBlankPassword}</span></label>
              <input className="mi" type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="••••••••" /></div>}
            <div className="mf full"><label>Phone <span style={{color:'rgba(255,255,255,.3)',fontWeight:400}}>(enables SMS login)</span></label>
              <input className="mi" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="09xx xxx xxxx" /></div>
            <div className="mf full">
              <label>{T.adminRole || 'Role'}</label>
              <div style={{display:'flex',gap:'.5rem',flexWrap:'wrap',marginTop:'.25rem'}}>
                {[['admin', T.roleStandard || 'Standard admin'], ['seo', T.roleSeo || 'SEO admin (full access)']].map(([val,lbl]) => (
                  <button key={val} type="button"
                    style={{padding:'.4rem .9rem',borderRadius:20,border:`1px solid ${form.role===val?'#6366f1':'rgba(99,102,241,.3)'}`,background:form.role===val?'rgba(99,102,241,.25)':'rgba(255,255,255,.04)',color:form.role===val?'#a5b4fc':'rgba(255,255,255,.5)',cursor:'pointer',fontSize:'.78rem',fontWeight:700,fontFamily:"'DM Sans',sans-serif"}}
                    onClick={()=>setForm(f=>({...f,role:val}))}>{lbl}</button>
                ))}
              </div>
              <span style={{fontSize:'.72rem',color:'rgba(255,255,255,.35)',display:'block',marginTop:'.4rem'}}>{T.roleSeoHint || 'SEO admins get full access to every tool and setting — only managing other admin accounts stays root-only.'}</span>
            </div>
            {form.role !== 'seo' && (
            <div className="mf full">
              <label>{T.permissions}</label>
              <div style={{display:'flex',gap:'.5rem',flexWrap:'wrap',marginTop:'.25rem'}}>
                {ALL_PERMISSIONS.map(p => (
                  <button key={p} type="button"
                    style={{padding:'.35rem .8rem',borderRadius:20,border:'1px solid rgba(99,102,241,.3)',background:form.permissions[p]?'rgba(99,102,241,.25)':'rgba(255,255,255,.04)',color:form.permissions[p]?'#a5b4fc':'rgba(255,255,255,.4)',cursor:'pointer',fontSize:'.78rem',fontFamily:"'DM Sans',sans-serif",transition:'all .2s'}}
                    onClick={()=>toggle(p)}>{p} {form.permissions[p]?'✓':''}</button>
                ))}
              </div>
            </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-cancel" onClick={onClose}>{T.cancel}</button>
          <button className="modal-save" onClick={handle}>{isEdit ? T.saveChanges : T.createAdminBtn}</button>
        </div>
        <ModalCSS />
      </div>
    </div>
  );
}

// ── Restaurant Edit Modal ─────────────────────────────────────────────────────
function RestaurantEditModal({ rest, onSave, onClose }) {
  const T = useCoreT();
  const [form, setForm] = useState({
    name: rest.name, isActive: rest.isActive,
    languageMode: rest.languageMode || 'single',
    defaultLanguage: rest.defaultLanguage || 'en',
    enabledLanguages: rest.enabledLanguages || ['en'],
  });

  const toggleLang = (code) => setForm(f => {
    const has = f.enabledLanguages.includes(code);
    if (has && f.enabledLanguages.length === 1) return f;
    return { ...f, enabledLanguages: has ? f.enabledLanguages.filter(c=>c!==code) : [...f.enabledLanguages,code] };
  });

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e=>e.stopPropagation()} style={{'--pri':'#6366f1'}}>
        <div className="modal-header">
          <h2>Edit Restaurant — {rest.slug}</h2>
          <button className="modal-x" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="modal-grid">
            <div className="mf full"><label>{T.restaurantName}</label>
              <input className="mi" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></div>
            <div className="mf full">
              <label>{T.status}</label>
              <button type="button" className={`pa-active-badge ${form.isActive?'on':'off'}`}
                style={{border:'none',cursor:'pointer',padding:'.45rem 1rem',borderRadius:10}}
                onClick={()=>setForm(f=>({...f,isActive:!f.isActive}))}>
                {form.isActive ? `✓ ${T.active}` : `✗ ${T.inactive}`}
              </button>
            </div>
            <div className="mf full"><label>{T.languageMode}</label>
              <div style={{display:'flex',gap:'.5rem',marginTop:'.25rem'}}>
                {['single','multi'].map(m=>(
                  <button key={m} type="button"
                    style={{padding:'.45rem 1.2rem',borderRadius:20,border:'1px solid',borderColor:form.languageMode===m?'#6366f1':'rgba(255,255,255,.12)',background:form.languageMode===m?'rgba(99,102,241,.2)':'rgba(255,255,255,.04)',color:form.languageMode===m?'#a5b4fc':'rgba(255,255,255,.5)',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",fontSize:'.85rem'}}
                    onClick={()=>setForm(f=>({...f,languageMode:m}))}>
                    {m==='single'?'🔤 Single':'🌐 Multi'}
                  </button>
                ))}
              </div>
            </div>
            <div className="mf full"><label>{T.defaultLanguage}</label>
              <div style={{display:'flex',gap:'.4rem',flexWrap:'wrap',marginTop:'.25rem'}}>
                {Object.values(LANGUAGES).map(lang=>(
                  <button key={lang.code} type="button"
                    style={{padding:'.35rem .7rem',borderRadius:10,border:'1px solid',borderColor:form.defaultLanguage===lang.code?'#6366f1':'rgba(255,255,255,.1)',background:form.defaultLanguage===lang.code?'rgba(99,102,241,.2)':'rgba(255,255,255,.04)',color:form.defaultLanguage===lang.code?'#a5b4fc':'rgba(255,255,255,.5)',cursor:'pointer',fontSize:'.82rem',fontFamily:"'DM Sans',sans-serif"}}
                    onClick={()=>setForm(f=>({...f,defaultLanguage:lang.code}))}>
                    {lang.flag} {lang.name}
                  </button>
                ))}
              </div>
            </div>
            {form.languageMode==='multi' && (
              <div className="mf full"><label>{T.enabledLanguages}</label>
                <div style={{display:'flex',gap:'.4rem',flexWrap:'wrap',marginTop:'.25rem'}}>
                  {Object.values(LANGUAGES).map(lang=>(
                    <button key={lang.code} type="button"
                      style={{padding:'.35rem .7rem',borderRadius:10,border:'1px solid',borderColor:form.enabledLanguages.includes(lang.code)?'#6366f1':'rgba(255,255,255,.1)',background:form.enabledLanguages.includes(lang.code)?'rgba(99,102,241,.2)':'rgba(255,255,255,.04)',color:form.enabledLanguages.includes(lang.code)?'#a5b4fc':'rgba(255,255,255,.5)',cursor:'pointer',fontSize:'.82rem',fontFamily:"'DM Sans',sans-serif"}}
                      onClick={()=>toggleLang(lang.code)}>
                      {lang.flag} {lang.name} {form.enabledLanguages.includes(lang.code)?'✓':''}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-cancel" onClick={onClose}>{T.cancel}</button>
          <button className="modal-save" onClick={()=>onSave(rest._id, form)}>{T.saveChanges}</button>
        </div>
        <ModalCSS />
      </div>
    </div>
  );
}

function ModalCSS() {
  return <style>{`
    .modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.8);backdrop-filter:blur(8px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:1rem}
    .modal-box{background:#12121f;border:1px solid rgba(99,102,241,.2);border-radius:20px;width:100%;max-width:560px;max-height:88vh;display:flex;flex-direction:column;overflow:hidden}
    .modal-header{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 1.5rem;border-bottom:1px solid rgba(255,255,255,.07)}
    .modal-header h2{font-family:'Playfair Display',serif;color:#fff;font-size:1.1rem}
    .modal-x{background:none;border:none;color:rgba(255,255,255,.4);cursor:pointer;font-size:1rem;padding:.25rem .5rem;border-radius:6px;transition:color .2s}
    .modal-x:hover{color:#fff}
    .modal-body{padding:1.5rem;overflow-y:auto;flex:1}
    .modal-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
    .mf{display:flex;flex-direction:column;gap:.4rem}
    .mf.full{grid-column:1/-1}
    .mf label{font-size:.72rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.07em}
    .mi{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:10px;padding:.75rem 1rem;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;outline:none;width:100%;transition:border-color .2s}
    .mi:focus{border-color:var(--pri)}
    .modal-footer{display:flex;gap:.75rem;justify-content:flex-end;padding:1.25rem 1.5rem;border-top:1px solid rgba(255,255,255,.07)}
    .modal-cancel{padding:.7rem 1.4rem;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.12);border-radius:10px;color:rgba(255,255,255,.6);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.9rem;transition:all .2s}
    .modal-cancel:hover{background:rgba(255,255,255,.09);color:#fff}
    .modal-save{padding:.7rem 1.75rem;background:var(--pri);border:none;border-radius:10px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;font-weight:700;cursor:pointer;transition:opacity .2s}
    .modal-save:hover{opacity:.85}
    .pa-active-badge.on{background:rgba(16,185,129,.15);color:#6ee7b7;border:none}
    .pa-active-badge.off{background:rgba(239,68,68,.15);color:#fca5a5;border:none}
  `}</style>;
}
